import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader from '../../components/AppHeader/AppHeader';
import UserService, { User, CreateUserRequest, UserListResponse } from '../../services/UserService';
import { formatName, splitFullName } from '../../utils/formatName';
import { formatNameByCountry } from '../../utils/nameFormatter';
import { utils } from '@ohif/core';
import ClientService, {
  Client,
  CreateClientRequest,
  UpdateClientRequest,
  ClientListResponse,
} from '../../services/ClientService';
import ProtocolService, {
  Protocol,
  ProtocolListResponse,
  GetProtocolsRequest,
} from '../../services/ProtocolService';
import RequirementService, {
  Requirement,
  RequirementListResponse,
  GetRequirementsRequest,
  CreateRequirementRequest,
  UpdateRequirementRequest,
  WorkOrder,
  ClientRequirementDocument,
} from '../../services/RequirementService';
import ApiService from '../../services/ApiService';
import { PAGINATION_CONSTANTS } from '../../constants/pagination';
import './Admin.css';
import moment from 'moment';
import { Tooltip, TooltipTrigger, TooltipContent, Icons, useNotification } from '@ohif/ui-next';
import AppTooltip from '../../components/AppTooltip/AppTooltip';
import Pagination from '../../components/Pagination';
import loadingGif from '../../../assets/loading.gif';
import { COUNTRY_CODES, DEFAULT_COUNTRY_CODE } from '../../constants/countryCodes';
import { REPORTING_FORMAT_OPTIONS } from '../../constants/reportingFormat';
import { NUMERIC_UNIT_OPTIONS } from '../../constants/numericUnits';
import de from 'platform/i18n/src/locales/de';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/free-regular-svg-icons';

const SHOW_SEND_FTP_BUTTON = false;
const PROTOCOL_SFTP_ENDPOINT = '/protocols/send-path';

// Common tooltip wrapper for table cells
const CellTooltip = ({ tooltip, children }) => (
  <AppTooltip content={tooltip}>
    <span className="cell-tooltip-text">{children}</span>
  </AppTooltip>
);

const PASSWORD_POLICIES = [
  'Minimum 8 characters, maximum 30 characters',
  'At least one lowercase letter (a-z)',
  'At least one uppercase letter (A-Z)',
  'At least one number (0-9)',
  'At least one special character (!@#$%^&*)',
];

// Multi-Select dropdown for imaging modalities with search, chips, and checkbox list
const ModalityMultiSelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  const availableModalities = [
    'CT (Computed Tomography)',
    'MRI (Magnetic Resonance Imaging)',
    'Ultrasound',
    'OCT (Optical Coherence Tomography)',
    'Fundus Photography',
    'Angiography',
  ];

  useEffect(() => {
    const handleClickOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleModality = modality => {
    const exists = value.includes(modality);
    const next = exists ? value.filter(v => v !== modality) : [...value, modality];
    onChange(next);
  };

  const removeModality = (modality, e) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== modality));
  };

  const clearAll = () => onChange([]);

  const filtered = availableModalities.filter(m =>
    m.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div>
      <div
        ref={containerRef}
        className="ms-container"
      >
        <div
          className={`ms-control ${open ? 'open' : ''}`}
          onClick={() => setOpen(v => !v)}
        >
          <div className="ms-chips">
            <span className="ms-placeholder">Select modalities…</span>
          </div>
          <span className="ms-caret">▾</span>
        </div>
        {open && (
          <div className="ms-dropdown">
            <div className="ms-toolbar">
              <input
                className="ms-search"
                placeholder="Search…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
              <AppTooltip content="Clear">
                <button
                  className="ms-clear"
                  onClick={e => {
                    e.stopPropagation();
                    clearAll();
                  }}
                >
                  ×
                </button>
              </AppTooltip>
            </div>
            <div className="ms-list">
              {filtered.map(m => {
                const checked = value.includes(m);
                return (
                  <label
                    key={m}
                    className="ms-item"
                    onClick={e => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleModality(m)}
                    />
                    <span className="ms-item-label">{m}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {value.length > 0 && (
        <div className="modality-tags">
          {value.map((modality, index) => (
            <span key={index} className="param-chip">
              {modality}
              <button
                type="button"
                onClick={(e) => removeModality(modality, e)}
                className="remove-tag"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Multi-Select dropdown for file formats with search and checkbox list
const FileFormatMultiSelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  const availableFormats = [
    'DICOM',
    'JPEG',
    'PNG',
    'TIFF',
    'PDF',
    'MP4',
    'AVI',
  ];

  useEffect(() => {
    const handleClickOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleFormat = format => {
    const exists = value.includes(format);
    const next = exists ? value.filter(v => v !== format) : [...value, format];
    onChange(next);
  };

  const removeFormat = (format, e) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== format));
  };

  const clearAll = () => onChange([]);

  const filtered = availableFormats.filter(f =>
    f.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div>
      <div
        ref={containerRef}
        className="ms-container"
      >
        <div
          className={`ms-control ${open ? 'open' : ''}`}
          onClick={() => setOpen(v => !v)}
        >
          <div className="ms-chips">
            <span className="ms-placeholder">Select file formats…</span>
          </div>
          <span className="ms-caret">▾</span>
        </div>
        {open && (
          <div className="ms-dropdown">
            <div className="ms-toolbar">
              <input
                className="ms-search"
                placeholder="Search…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="ms-clear"
                    onClick={e => {
                      e.stopPropagation();
                      clearAll();
                    }}
                  >
                    ×
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="tooltip-ui"
                >
                  Clear
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="ms-list">
              {filtered.map(f => {
                const checked = value.includes(f);
                return (
                  <label
                    key={f}
                    className="ms-item"
                    onClick={e => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleFormat(f)}
                    />
                    <span className="ms-item-label">{f}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {value.length > 0 && (
        <div className="modality-tags">
          {value.map((format, index) => (
            <span key={index} className="param-chip">
              {format}
              <button
                type="button"
                onClick={(e) => removeFormat(format, e)}
                className="remove-tag"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Searchable dropdown for CRD ID selection
const CrdIdSearchableDropdown = ({ value, onChange, options, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery(''); // Reset search on close
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = options.filter(id =>
    id.toLowerCase().includes(query.toLowerCase())
  );

  const selectOption = (id) => {
    onChange(id);
    setOpen(false);
    setQuery('');
  };

  return (
    <div
      ref={containerRef}
      className="ms-container"
      style={{ position: 'relative' }}
    >
      <div
        className={`ms-control ${open ? 'open' : ''}`}
        onClick={() => !disabled && setOpen(v => !v)}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1
        }}
      >
        <div className="ms-chips">
          <span className={value ? 'ms-selected' : 'ms-placeholder'}>
            {value || 'Select CRD ID...'}
          </span>
        </div>
        <span className="ms-caret">▾</span>
      </div>
      {open && !disabled && (
        <div className="ms-dropdown">
          <div className="ms-toolbar">
            <input
              className="ms-search"
              placeholder="Search CRD ID..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              onClick={e => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="ms-list" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div className="ms-empty">No CRD IDs found</div>
            ) : (
              filtered.map(id => (
                <div
                  key={id}
                  className="ms-item"
                  onClick={() => selectOption(id)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: id === value ? '#1f2937' : 'transparent'
                  }}
                >
                  <span className="ms-item-label">{id}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  totalJobs: number;
  completedJobs: number;
  pendingJobs: number;
}

// Multi-Select dropdown for graders with search, chips, and checkbox list
const GraderMultiSelect = ({ value, onChange, options, disabled }: { value: number[]; onChange: (ids: number[]) => void; options: { id: number; username: string }[]; disabled?: boolean }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (containerRef.current && !(containerRef.current as any).contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleId = (id: number) => {
    const exists = value.includes(id);
    const next = exists ? value.filter(v => v !== id) : [...value, id];
    onChange(next);
  };

  const removeId = (id: number, e: any) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== id));
  };

  const clearAll = (e: any) => {
    e.stopPropagation();
    onChange([]);
  };

  const filtered = options.filter(o => o.username.toLowerCase().includes(query.toLowerCase()));
  const selectedUsers = value.map(id => options.find(o => o.id === id)).filter(Boolean) as { id: number; username: string }[];

  return (
    <>
    <div className="ms-container" ref={containerRef}>
      <div
        className={`ms-control ${open ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setOpen(prev => !prev)}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
      >
        <span className="ms-placeholder">Select graders…</span>
        <span className="ms-caret">▾</span>
      </div>

      {open && !disabled && (
        <div className="ms-dropdown" onClick={e => e.stopPropagation()}>
          <div className="ms-search">
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search graders"
            />
            <button className="ms-clear" onClick={clearAll} title="Clear">Clear</button>
          </div>
          <div className="ms-list">
            {filtered.length === 0 && (
              <div className="ms-empty">No graders found</div>
            )}
            {filtered.map(o => {
              const checked = value.includes(o.id);
              return (
                <label key={o.id} className={`ms-item ${checked ? 'checked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleId(o.id)}
                  />
                  <span className="ms-item-label">{o.username}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

    </div>
    {/* Selected chips listed outside below input and outside positioned container to avoid pushing dropdown */}
    <div className="ms-selected-chips">
      {selectedUsers.map(u => (
        <span key={u.id} className="param-chip">
          {u.username}
          {!disabled && (
            <button type="button" onClick={e => removeId(u.id, e)} className="remove-tag">×</button>
          )}
        </span>
      ))}
    </div>
    </>
  );
};

// Single-Select dropdown for escalate to person with search
const EscalateToSelect = ({ value, onChange, options, disabled }: { value: number | null; onChange: (id: number | null) => void; options: { id: number; username: string }[]; disabled?: boolean }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (containerRef.current && !(containerRef.current as any).contains(event.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectOption = (id: number) => {
    onChange(id);
    setOpen(false);
    setQuery('');
  };

  const clearSelection = (e: any) => {
    e.stopPropagation();
    onChange(null);
    setQuery('');
  };

  const filtered = options.filter(o => o.username.toLowerCase().includes(query.toLowerCase()));
  const selectedUser = value ? options.find(o => o.id === value) : null;

  return (
    <div className="ms-container" ref={containerRef}>
      <div
        className={`ms-control ${open ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setOpen(prev => !prev)}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
      >
        <span className={selectedUser ? 'ms-selected' : 'ms-placeholder'}>
          {selectedUser ? selectedUser.username : 'Select person…'}
        </span>
        <span className="ms-caret">▾</span>
      </div>

      {open && !disabled && (
        <div className="ms-dropdown" onClick={e => e.stopPropagation()}>
          <div className="ms-search">
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search person"
            />
            {selectedUser && (
              <button className="ms-clear" onClick={clearSelection} title="Clear">Clear</button>
            )}
          </div>
          <div className="ms-list">
            {filtered.length === 0 && (
              <div className="ms-empty">No person found</div>
            )}
            {filtered.map(o => {
              const isSelected = value === o.id;
              return (
                <label
                  key={o.id}
                  className={`ms-item ${isSelected ? 'checked' : ''}`}
                  onClick={() => selectOption(o.id)}
                >
                  <input
                    type="radio"
                    checked={isSelected}
                    onChange={() => selectOption(o.id)}
                  />
                  <span className="ms-item-label">{o.username}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const Admin: React.FC = () => {
  const { user } = useAuth();
  const notification = useNotification();
  const location = useLocation();
  const navigate = useNavigate();

  const getInitialTabFromPath = (pathname: string): 'users' | 'clients' | 'protocol' | 'forms' | 'settings' => {
    const segments = pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1];

    switch (last) {
      case 'clients':
        return 'clients';
      case 'requirements':
        return 'protocol'; // Requirements tab
      case 'protocols':
        return 'forms'; // Protocols tab
      case 'settings':
        return 'settings';
      // '/admin' or unknown segment defaults to users
      default:
        return 'users';
    }
  };

  const [activeTab, setActiveTab] = useState<
    'users' | 'clients' | 'protocol' | 'forms' | 'settings'
  >(() => getInitialTabFromPath(location.pathname));
  const [users, setUsers] = useState<User[]>([]);
  const [userListResponse, setUserListResponse] = useState<UserListResponse | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [clientListResponse, setClientListResponse] = useState<ClientListResponse | null>(null);
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [protocolListResponse, setProtocolListResponse] = useState<ProtocolListResponse | null>(
    null
  );
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [requirementListResponse, setRequirementListResponse] = useState<RequirementListResponse | null>(
    null
  );
  const [showRequirementEditor, setShowRequirementEditor] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState<Requirement | null>(null);
  const [showProtocolEditor, setShowProtocolEditor] = useState(false);
  const [editingProtocol, setEditingProtocol] = useState<Protocol | null>(null);
  const [isDuplicatingProtocol, setIsDuplicatingProtocol] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [showRequirementForm, setShowRequirementForm] = useState(false);
  const [editingRequirementForm, setEditingRequirementForm] = useState<Requirement | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE);
  const [userService] = useState(() => UserService.getInstance());
  const [clientService] = useState(() => ClientService.getInstance());
  const [protocolService] = useState(() => ProtocolService.getInstance());
  const [availableCrdIds, setAvailableCrdIds] = useState<string[]>([]);
  const [availableCrdDocs, setAvailableCrdDocs] = useState<{ id: number; crd_id: string }[]>([]);
  const [availableGraders, setAvailableGraders] = useState<{ id: number; username: string }[]>([]);
  const [availableManagers, setAvailableManagers] = useState<{ id: number; username: string }[]>([]);
  const [requirementService] = useState(() => RequirementService.getInstance());
  const [sendingProtocolId, setSendingProtocolId] = useState<string | null>(null);
  const [printingProtocolId, setPrintingProtocolId] = useState<string | null>(null);
  const [confirmState, setConfirmState] = useState<null | {
    kind: 'user' | 'client' | 'protocol' | 'requirement' | 'ftp-send' | 'ftp-reset' | 'protocol-sftp' | 'user-reset';
    id: string | number;
    label: string;
    requirementTitle?: string; // For requirement delete message
    clientName?: string; // For requirement delete message
    clientData?: Client; // For SFTP actions
    protocolData?: Protocol; // For protocol SFTP actions
  }>(null);

  // Search and filter states for users
  const [searchUsername, setSearchUsername] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');

  // Search and filter states for clients
  const [searchClientName, setSearchClientName] = useState('');
  const [selectedClientStatus, setSelectedClientStatus] = useState('all');

  // Search and filter states for protocols
  const [searchProtocolQuery, setSearchProtocolQuery] = useState('');
  const [selectedProtocolStatus, setSelectedProtocolStatus] = useState('all');
  const [selectedProtocolActive, setSelectedProtocolActive] = useState('all');
  const [selectedProtocolClient, setSelectedProtocolClient] = useState('all');

  // Search and filter states for requirements
  const [searchCrdId, setSearchCrdId] = useState(''); // Combined search for CRD ID / Work Order ID
  const [searchRequirementClientName, setSearchRequirementClientName] = useState('all');

  // Client list for dropdowns
  const [clientsForDropdown, setClientsForDropdown] = useState<Client[]>([]);
  const [searchStartDate, setSearchStartDate] = useState('');
  const [searchCompletionDate, setSearchCompletionDate] = useState('');
  const [searchStatus, setSearchStatus] = useState('all');
  const [searchSignOffDate, setSearchSignOffDate] = useState('');

  useEffect(() => {
    // Prevent background scrolling when any modal is open
    const hasOpenModal = showAddUserModal || showAddClientModal || !!confirmState;
    if (hasOpenModal) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
  }, [showAddUserModal, showAddClientModal, confirmState]);

  useEffect(() => {
    if (activeTab === 'users') {
      // Reset user filters to defaults when switching to Users tab
      setSearchUsername('');
      setSelectedStatus('all');
      setSelectedRole('all');
      setCurrentPage(1);
      loadUsers(1, pageSize, '', 'all', 'all');
    } else if (activeTab === 'clients') {
      // Reset client filters to defaults when switching to Clients tab
      setSearchClientName('');
      setSelectedClientStatus('all');
      setCurrentPage(1);
      loadClients(1, 5, 'all', 'all');
    } else if (activeTab === 'protocol') {
      // Reset requirement filters and close forms when switching to Protocol tab
      setSearchCrdId('');
      setSearchRequirementClientName('all');
      setSearchStartDate('');
      setSearchCompletionDate('');
      setSearchStatus('all');
      setSearchSignOffDate('');
      setCurrentPage(1);
      setShowRequirementForm(false);
      setEditingRequirementForm(null);
      // Load clients for requirement-related dropdowns
      loadClientsForDropdown();
      loadRequirements(1, pageSize, '', 'all', 'all', '', '', '');
    } else if (activeTab === 'forms') {
      // Ensure protocol list view on entry
      setShowProtocolEditor(false);
      setConfirmState(null);
      setCurrentPage(1);
      // Reset protocol filters to defaults when entering forms tab
      setSearchProtocolQuery('');
      setSelectedProtocolStatus('all');
      setSelectedProtocolActive('all');
      setSelectedProtocolClient('all');
      // Load clients so we can show client names in the protocol listing
      loadClientsForDropdown();
      loadProtocols(1, pageSize, null, 'all', 'all', '', 'all');
    } else {
      // Ensure protocol editor/confirm closed for other tabs
      setShowProtocolEditor(false);
      setConfirmState(null);
      loadProtocols(currentPage, pageSize, null, 'all', 'all', '', 'all');
    }
  }, [activeTab, pageSize]);

  // Clear protocol filters when navigating away from forms/protocol tab
  useEffect(() => {
    // When activeTab changes away from 'forms', clear protocol filters
    if (activeTab !== 'forms') {
      setSearchProtocolQuery('');
      setSelectedProtocolStatus('all');
      setSelectedProtocolActive('all');
      setSelectedProtocolClient('all');
    }
  }, [activeTab]);

  // Clear protocol filters when route changes away from protocol-related routes
  useEffect(() => {
    const currentPath = location.pathname.toLowerCase();
    // Check if we're on a protocol-related route
    const isProtocolRoute = currentPath.includes('/admin/protocols') ||
                            currentPath.includes('/admin/forms') ||
                            (currentPath.startsWith('/admin') && activeTab === 'forms');

    if (!isProtocolRoute && currentPath.startsWith('/admin')) {
      // Clear protocol filters when navigating away from protocol routes within admin
      setSearchProtocolQuery('');
      setSelectedProtocolStatus('all');
      setSelectedProtocolActive('all');
      setSelectedProtocolClient('all');
    }
  }, [location.pathname, activeTab]);

  // Preload CRD IDs when entering Protocols tab so Add Protocol opens instantly
  useEffect(() => {
    if (activeTab === 'forms') {
      (async () => {
        try {
          const resp = await protocolService.getRequirementDocs();
          if (resp.is_success && Array.isArray(resp.data) && resp.data.length > 0) {
            const docs = resp.data[0].requirement_docs || [];
            // Filter only APPROVED CRDs (case-insensitive)
            const ids = docs
              .filter((d: any) => d && d.crd_id && d.status && d.status.toLowerCase() === 'approved')
              .map((d: any) => String(d.crd_id));
            const docsWithMapping = docs
              .filter((d: any) => d && d.crd_id && d.id && d.status && d.status.toLowerCase() === 'approved')
              .map((d: any) => ({ id: d.id, crd_id: String(d.crd_id) }));
            setAvailableCrdIds(ids);
            setAvailableCrdDocs(docsWithMapping);
            // broadcast for ProtocolEditor fallback
            window.dispatchEvent(new CustomEvent('crd-list-preloaded', { detail: { crdIds: ids, crdDocs: docsWithMapping } }));
          }
          // Preload graders
          try {
            const api = ApiService.getInstance();
            const gradersResp = await api.get<any>(
              '/grader/get-users',
              undefined,
              api.getAuthServiceUrl()
            );
            if (gradersResp.success) {
              const list = Array.isArray(gradersResp.data) ? gradersResp.data : gradersResp.data?.data || [];
              const mapped = list.map((u: any) => ({ id: Number(u.id ?? u.user_id ?? u.ID ?? 0), username: String(u.username ?? u.name ?? '').trim() }))
                .filter((u: any) => u.id && u.username);
              setAvailableGraders(mapped);
              window.dispatchEvent(new CustomEvent('graders-preloaded', { detail: { graders: mapped } }));
            }
          } catch (err) {
            console.warn('Failed to preload graders:', err);
          }

          // Preload managers for Escalate To dropdown
          try {
            const api = ApiService.getInstance();
            const managersResp = await api.get<any>(
              '/grader/get-managers',
              undefined,
              api.getAuthServiceUrl()
            );
            if (managersResp.success) {
              const rawData = Array.isArray(managersResp.data?.data)
                ? managersResp.data.data
                : Array.isArray(managersResp.data)
                  ? managersResp.data
                  : [];

              let usersList: any[] = [];

              if (Array.isArray(rawData)) {
                rawData.forEach(item => {
                  if (Array.isArray(item?.users)) {
                    usersList = usersList.concat(item.users);
                  } else if (item) {
                    usersList.push(item);
                  }
                });
              } else if (Array.isArray(rawData?.users)) {
                usersList = rawData.users;
              }

              const mapped = usersList
                .map((u: any) => {
                  const formattedName = formatNameByCountry(
                    u.first_name || '',
                    u.last_name || '',
                    u.middle_name || '',
                    'IND'
                  );
                  const displayName = formattedName !== '-' ? formattedName : (u.username || '-');

                  return {
                    id: Number(u.id ?? u.user_id ?? u.ID ?? 0),
                    username: String(displayName).trim(),
                  };
                })
                .filter((u: any) => u.id && u.username && u.username !== '-');

              setAvailableManagers(mapped);
            }
          } catch (err) {
            console.warn('Failed to preload managers:', err);
          }
        } catch (e) {
          console.error('Failed to preload CRD IDs:', e);
        }
      })();
    }
  }, [activeTab]);

  // Reload data when currentPage changes to ensure API pagination works across tabs
  useEffect(() => {
    // Avoid running before activeTab is initialized
    if (!activeTab) {
      return;
    }

    if (activeTab === 'users') {
      loadUsers(
        currentPage,
        pageSize,
        (searchUsername || '').trim(),
        selectedStatus,
        selectedRole
      );
    } else if (activeTab === 'clients') {
      loadClients(
        currentPage,
        5,
        (searchClientName || '').trim() || 'all',
        selectedClientStatus
      );
    } else if (activeTab === 'forms') {
      // Protocols tab (forms)
      setShowProtocolEditor(false);
      setConfirmState(null);
      loadProtocols(
        currentPage,
        pageSize,
        (searchProtocolQuery || '').trim() || null,
        selectedProtocolStatus,
        selectedProtocolActive,
        '',
        selectedProtocolClient
      );
    } else if (activeTab === 'protocol') {
      // Requirements tab is keyed as 'protocol' in this view
      const searchQuery = (searchCrdId || '').trim() || '';
      loadRequirements(
        currentPage,
        pageSize,
        searchQuery,
        searchRequirementClientName === 'all' ? 'all' : searchRequirementClientName,
        searchStatus,
        searchStartDate,
        searchCompletionDate,
        searchSignOffDate
      );
    }
  }, [currentPage]);

  // Auto-search for clients when search input or status changes
  useEffect(() => {
    if (activeTab === 'clients') {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadClients(1, 5, searchClientName.trim() || 'all', selectedClientStatus);
      }
    }
  }, [searchClientName, selectedClientStatus]);

  // Auto-search for users when search input or filters change
  useEffect(() => {
    if (activeTab === 'users') {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadUsers(1, pageSize, searchUsername.trim(), selectedStatus, selectedRole);
      }
    }
  }, [searchUsername, selectedStatus, selectedRole]);

  // Auto-search for requirements when search input or filters change
  useEffect(() => {
    if (activeTab === 'protocol') {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        const searchQuery = searchCrdId.trim();
        loadRequirements(
          1,
          pageSize,
          searchQuery,
          searchRequirementClientName === 'all' ? 'all' : searchRequirementClientName,
          searchStatus,
          searchStartDate,
          searchCompletionDate,
          searchSignOffDate
        );
      }
    }
  }, [searchCrdId, searchRequirementClientName, searchStatus, searchStartDate, searchCompletionDate, searchSignOffDate]);

  // Auto-search for protocols when search input or filters change
  useEffect(() => {
    if (activeTab === 'forms') {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadProtocols(
          1,
          pageSize,
          searchProtocolQuery.trim() || null,
          selectedProtocolStatus,
          selectedProtocolActive,
          '',
          selectedProtocolClient
        );
      }
    }
  }, [searchProtocolQuery, selectedProtocolStatus, selectedProtocolActive, selectedProtocolClient]);

  const loadUsers = async (
    page: number = 1,
    size: number = PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE,
    username: string = 'all',
    status: string = 'all',
    role: string = 'all'
  ) => {
    setLoading(true);
    try {
      const filterParams = {
        user_id: 'all',
        username: username,
        page: page,
        size: size,
        is_active: status === 'all' ? 'all' : status === 'active',
        role: role,
      };

      const response = await userService.getUsers(filterParams);

      if (response.success && response.data) {
        setUserListResponse(response.data);
        setUsers(response.data.users);
      } else {
        console.error('Failed to load users:', response.error);
        // Fallback to empty state
        setUsers([]);
        setUserListResponse(null);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
      setUserListResponse(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    // Search works with any combination of filters
    setCurrentPage(1); // Reset to first page when searching
    loadUsers(
      1,
      pageSize,
      searchUsername.trim(),
      selectedStatus,
      selectedRole
    );
  };

  const handleClear = () => {
    setSearchUsername('');
    setSelectedStatus('all');
    setSelectedRole('all');
    setCurrentPage(1);
    loadUsers(1, pageSize, '', 'all', 'all');
  };

  // Status and role changes now handled by useEffect above
  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
  };

  const handleRoleChange = (value: string) => {
    setSelectedRole(value);
  };

  const loadClients = async (
    page: number = 1,
    size: number = 5,
    clientName: string = 'all',
    status: string = 'all'
  ) => {
    setLoading(true);
    try {
      const response = await clientService.getClientsForAdmin({
        search: clientName === 'all' ? '' : clientName,
        org_id: '',
        status: status,
        page: page,
        size: size,
      });

      if (response.success && response.data) {
        setClientListResponse(response.data);
        setClients(response.data.clients || []);
      } else {
        console.error('Failed to load clients:', response.error);
        setClients([]);
        // Set a default response object to ensure pagination shows correctly
        setClientListResponse({
          clients: [],
          total_count: 0,
          page: 1,
          page_size: 5,
          size: 5,
          total_pages: 0
        });
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
      // Set a default response object to ensure pagination shows correctly
      setClientListResponse({
        clients: [],
        total_count: 0,
        page: 1,
        page_size: 5,
        size: 5,
        total_pages: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClientSearch = () => {
    if (searchClientName.trim() || selectedClientStatus !== 'all') {
      setCurrentPage(1);
      loadClients(1, 5, searchClientName.trim() || 'all', selectedClientStatus);
    }
  };

  const handleClientClear = () => {
    setSearchClientName('');
    setSelectedClientStatus('all');
    setCurrentPage(1);
    loadClients(1, 5, 'all', 'all');
  };

  const loadClientsForDropdown = async () => {
    try {
      const response = await clientService.getClients({
        search: '',
        is_active: true,
        page: 1,
        size: 100 // Get more clients for dropdown
      });

      if (response.success && response.data) {
        setClientsForDropdown(response.data.clients);
      }
    } catch (error) {
      console.error('Error loading clients for dropdown:', error);
    }
  };

  const loadProtocols = async (
    page: number = 1,
    size: number = PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE,
    protocolName: string = null,
    status: string = 'all',
    isActive: string = 'all',
    paramQuery: string = '',
    clientId: string = 'all'
  ) => {
    setLoading(true);
    try {
      // Map filters to API expectations
      const statusParam = status === 'Published' || status === 'Draft' || status === 'Aborted' ? status : 'all';
      const isActiveParam =
        isActive === 'active' ? 'true' : isActive === 'inactive' ? 'false' : 'all';
      const clientIdParam = clientId === 'all' ? 'all' : clientId;

      // Build request; include search ONLY when user typed something
      const request: any = {
        protocol_id: 'all',
        requirement_doc_id: 'all',
        status: statusParam,
        is_active: isActiveParam,
        client_id: clientIdParam,
        page: page,
        size: size,
      };
      const trimmed = (protocolName || '').trim();
      if (trimmed) {
        request.search = trimmed;
      }

      const response = await protocolService.getProtocols(request);

      if (response.is_success && response.data && response.data.length > 0) {
        const protocolData = response.data[0];
        setProtocolListResponse(protocolData);

        // Filter protocols by name/parameters and active status
        let filteredProtocols = protocolData.protocols;

        // Only apply search filter if protocolName or paramQuery has actual values
        if ((protocolName && protocolName !== 'all') || paramQuery) {
          const q = (paramQuery || protocolName || '').toLowerCase();
          const matchProtocol = (p: Protocol) => {
            const nameMatch = p.name.toLowerCase().includes(q);
            const collect = (nodes: any[]): string[] =>
              nodes.flatMap(n => [n.parameter_name, ...(n.children ? collect(n.children) : [])]);
            const paramNames = collect(p.parameters || []);
            const paramMatch = paramNames.some(n => (n || '').toLowerCase().includes(q));
            return nameMatch || paramMatch;
          };
          filteredProtocols = filteredProtocols.filter(matchProtocol);
        }
        if (isActive !== 'all') {
          const activeFilter = isActive === 'active';
          filteredProtocols = filteredProtocols.filter(p => p.is_active === activeFilter);
        }
        if (paramQuery) {
          const q = paramQuery.toLowerCase();
          const includesParam = (p: Protocol) => {
            const collect = (nodes: any[]): string[] =>
              nodes.flatMap(n => [n.parameter_name, ...(n.children ? collect(n.children) : [])]);
            const names = collect(p.parameters || []);
            return names.some(n => (n || '').toLowerCase().includes(q));
          };
          filteredProtocols = filteredProtocols.filter(includesParam);
        }

        setProtocols(filteredProtocols);
      } else {
        console.error('Failed to load protocols:', response.message);
        setProtocols([]);
        setProtocolListResponse(null);
      }
    } catch (error) {
      console.error('Error loading protocols:', error);
      setProtocols([]);
      setProtocolListResponse(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClientKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchClientName.trim()) {
      handleClientSearch();
    }
  };

  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setShowAddClientModal(true);
  };

  const handleDeleteClient = async (clientId: number) => {
    setConfirmState({
      kind: 'client',
      id: clientId,
      label: clients.find(c => (c.client_id || c.id) === clientId)?.name || 'client',
    });
  };

  const handleSendFtpCredentials = (client: Client) => {
    setConfirmState({
      kind: 'ftp-send',
      id: client.client_id || client.id!,
      label: client.name,
      clientData: client,
    });
  };

  const handleResetFtpPassword = (client: Client) => {
    setConfirmState({
      kind: 'ftp-reset',
      id: client.client_id || client.id!,
      label: client.name,
      clientData: client,
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchUsername.trim()) {
      handleSearch();
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowAddUserModal(true);
  };

  const handleDeleteUser = async (userId: string) => {
    const user = users.find(u => u.id == userId);
    let displayName = '';

    if (user) {
      // Check if any name fields have values (same logic as table)
      const hasName = user.first_name || user.last_name || user.middle_name;

      if (hasName) {
        // Use name formatter with India country code
        displayName = formatNameByCountry(
          user.first_name || '',
          user.last_name || '',
          user.middle_name || '',
          'IND'
        );
      } else {
        // Fallback to '-' if all name fields are null/empty
        displayName = '-';
      }
    }

    setConfirmState({
      kind: 'user',
      id: userId,
      label: displayName,
    });
  };

  const handleForcedReset = async (userId: string) => {
    const user = users.find(u => u.id == userId);
    let displayName = '';

    if (user) {
      // Check if any name fields have values (same logic as table)
      const hasName = user.first_name || user.last_name || user.middle_name;

      if (hasName) {
        // Use name formatter with India country code
        displayName = formatNameByCountry(
          user.first_name || '',
          user.last_name || '',
          user.middle_name || '',
          'IND'
        );
      } else {
        // Fallback to username if all name fields are null/empty
        displayName = user.username || '-';
      }
    }

    setConfirmState({
      kind: 'user-reset',
      id: userId,
      label: displayName,
    });
  };

  const handleProtocolSearch = () => {
    if (
      searchProtocolQuery.trim() ||
      selectedProtocolStatus !== 'all' ||
      selectedProtocolActive !== 'all' ||
      selectedProtocolClient !== 'all'
    ) {
      setCurrentPage(1);
      loadProtocols(
        1,
        pageSize,
        searchProtocolQuery.trim() || null,
        selectedProtocolStatus,
        selectedProtocolActive,
        searchProtocolQuery.trim(),
        selectedProtocolClient
      );
    }
  };

  const handleProtocolClear = () => {
    setSearchProtocolQuery('');
    setSelectedProtocolStatus('all');
    setSelectedProtocolActive('all');
    setSelectedProtocolClient('all');
    setCurrentPage(1);
    loadProtocols(1, pageSize, null, 'all', 'all', '', 'all');
  };

  const promptSendProtocolSftpDetails = (protocol: Protocol) => {
    const protocolId = protocol?.id ? String(protocol.id) : '';
    if (!protocolId) {
      notification.show({
        title: 'Unable to Send SFTP Details',
        message: 'Protocol identifier is missing.',
        type: 'error',
        position: 'top-right',
      });
      return;
    }

    setConfirmState({
      kind: 'protocol-sftp',
      id: protocolId,
      label: protocol.name || 'protocol',
      protocolData: protocol,
    });
  };

  const handlePrintProtocol = async (protocol: Protocol) => {
    const protocolId = protocol?.id ? String(protocol.id) : '';
    if (!protocolId) {
      notification.show({
        title: 'Unable to Print Protocol',
        message: 'Protocol identifier is missing.',
        type: 'error',
        position: 'top-right',
      });
      return;
    }

    if (printingProtocolId) {
      return;
    }

    setPrintingProtocolId(protocolId);
    try {
      const result = await protocolService.generateProtocolReport(protocolId);

      if (result.success) {
        notification.show({
          title: 'Protocol Report Ready',
          message: result.message,
          type: 'success',
          position: 'top-right',
          duration: 3000,
        });
      } else {
        notification.show({
          title: 'Failed to Print Protocol',
          message: result.message,
          type: 'error',
          position: 'top-right',
          duration: 4000,
        });
      }
    } catch (error) {
      console.error('Error printing protocol:', error);
      notification.show({
        title: 'Failed to Print Protocol',
        message:
          error instanceof Error ? error.message : 'Unexpected error occurred. Please try again.',
        type: 'error',
        position: 'top-right',
        duration: 4000,
      });
    } finally {
      setPrintingProtocolId(null);
    }
  };

  const sendProtocolSftpDetails = async (protocolId: string, protocolName?: string) => {
    if (!protocolId) {
      notification.show({
        title: 'Unable to Send SFTP Details',
        message: 'Protocol identifier is missing.',
        type: 'error',
        position: 'top-right',
      });
      return;
    }

    if (sendingProtocolId) {
      return;
    }

    setSendingProtocolId(protocolId);
    try {
      const api = ApiService.getInstance();
      const baseUrl = api.getProtocolServiceUrl();
      const response = await api.post<any>(
        PROTOCOL_SFTP_ENDPOINT,
        {
          protocol_id: protocolId,
        },
        baseUrl
      );

      const payload = response.data as { is_success?: boolean; message?: string; data?: string } | undefined;

      if (response.success && payload?.is_success) {
        notification.show({
          title: 'SFTP Path Sent',
          message: payload.message || `SFTP details sent for ${protocolName || 'protocol'}.`,
          type: 'success',
          position: 'top-right',
          duration: 3000,
        });
      } else {
        notification.show({
          title: 'Failed to Send SFTP Details',
          message: payload?.message || response.error || 'Please try again later.',
          type: 'error',
          position: 'top-right',
          duration: 4000,
        });
      }
    } catch (error) {
      console.error('Error sending protocol SFTP details:', error);
      notification.show({
        title: 'Failed to Send SFTP Details',
        message: error instanceof Error ? error.message : 'Unexpected error occurred. Please try again.',
        type: 'error',
        position: 'top-right',
        duration: 4000,
      });
    } finally {
      setSendingProtocolId(null);
    }
  };

  const handleProtocolKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchProtocolQuery.trim()) {
      handleProtocolSearch();
    }
  };

  const loadRequirements = async (
    page: number = 1,
    size: number = PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE,
    searchQuery: string = '', // Combined search for CRD ID / Work Order ID
    clientId: string = 'all',
    status: string = 'all',
    startDate: string = '',
    endDate: string = '',
    signedDate: string = ''
  ) => {
    setLoading(true);
    try {
      const params: any = {
        client_id: clientId,
        status: status,
        page: page,
        size: size
      };

      // Only add search if it has a value
      if (searchQuery && searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      // Only add dates if they have values
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }
      if (signedDate) {
        params.signed_date = signedDate;
      }

      const response = await requirementService.getRequirements(params);

      if (response.success && response.data) {
        setRequirementListResponse(response.data);
        setRequirements(response.data.requirements);
      } else {
        console.error('Failed to load requirements:', response.error);
        setRequirements([]);
        setRequirementListResponse(null);
      }
    } catch (error) {
      console.error('Error loading requirements:', error);
      setRequirements([]);
      setRequirementListResponse(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRequirementSearch = () => {
    setCurrentPage(1);
    // Use CRD ID / Work Order ID search as single search parameter
    const searchQuery = searchCrdId.trim();
    loadRequirements(
      1,
      pageSize,
      searchQuery,
      searchRequirementClientName === 'all' ? 'all' : searchRequirementClientName,
      searchStatus,
      searchStartDate,
      searchCompletionDate,
      searchSignOffDate
    );
  };

  const handleRequirementClear = () => {
    setSearchCrdId('');
    setSearchRequirementClientName('all');
    setSearchStartDate('');
    setSearchCompletionDate('');
    setSearchStatus('all');
    setSearchSignOffDate('');
    setCurrentPage(1);
    loadRequirements(1, pageSize, '', 'all', 'all', '', '', '');
  };

  const handleEditRequirement = (requirement: Requirement) => {
    setEditingRequirementForm(requirement);
    setShowRequirementForm(true);
  };

  const handleDeleteRequirement = async (requirementId: string) => {
    const requirement = requirements.find(r => r.id.toString() === requirementId);
    setConfirmState({
      kind: 'requirement',
      id: requirementId,
      label: requirement?.crdId || 'requirement',
      requirementTitle: requirement?.requirementTitle || 'Unknown Requirement',
      clientName: requirement?.clientName || 'Unknown Client',
    });
  };

  const formatRequirementStatus = (status?: string) => {
    if (!status) {
      return '-';
    }

    return status
      .replace(/_/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  };

  const handleDeleteProtocol = async (protocolId: number) => {
    setConfirmState({
      kind: 'protocol',
      id: protocolId.toString(),
      label: protocols.find(p => p.id === protocolId)?.name || 'protocol',
    });
  };

  const [isDeleting, setIsDeleting] = useState(false);

  const confirmDeletion = async () => {
    if (!confirmState) return;

    setIsDeleting(true);
    try {
      if (confirmState.kind === 'user') {
        const response = await userService.deleteUser(confirmState.id);

        // Close the dialog first
        setConfirmState(null);

        if (response.success) {
          // Show success toast
          notification.show({
            title: 'Successfully deleted',
            message: 'User has been removed',
            type: 'success',
            duration: 3000,
            position: 'top-right',
          });
          // Refresh user list
          loadUsers(currentPage, pageSize, searchUsername, selectedStatus, selectedRole);
        } else {
          // Show error toast
          notification.show({
            title: 'Failed to delete user',
            message: 'Please try later',
            type: 'error',
            duration: 5000,
            position: 'top-right',
          });
        }
      } else if (confirmState.kind === 'client') {
        const response = await clientService.deleteClient(confirmState.id as number);

        // Close the dialog first
        setConfirmState(null);

        if (response.success) {
          notification.show({
            title: 'Successfully deleted',
            message: 'Client has been removed',
            type: 'success',
            duration: 3000,
            position: 'top-right',
          });
          // Refresh client list
          loadClients(currentPage, 5, searchClientName, selectedClientStatus);
        } else {
          notification.show({
            title: 'Failed to delete client',
            message: response.error || 'Please try later',
            type: 'error',
            duration: 5000,
            position: 'top-right',
          });
        }
      } else if (confirmState.kind === 'protocol') {
        const response = await protocolService.deleteProtocol(Number(confirmState.id));

        // Close the dialog first
        setConfirmState(null);

        if (response.is_success) {
          // Show success toast
          notification.show({
            title: 'Successfully deleted',
            message: 'Protocol has been removed',
            type: 'success',
            duration: 3000,
            position: 'top-right',
          });
          loadProtocols(
            currentPage,
            pageSize,
            searchProtocolQuery || null,
            selectedProtocolStatus,
            selectedProtocolActive,
            searchProtocolQuery,
            selectedProtocolClient
          );
        } else {
          // Show error toast
          notification.show({
            title: 'Failed to delete protocol',
            message: 'Please try again later',
            type: 'error',
            duration: 5000,
            position: 'top-right',
          });
        }
      } else if (confirmState.kind === 'requirement') {
        const response = await requirementService.deleteRequirement(confirmState.id as string);

        // Close the dialog first
        setConfirmState(null);

        if (response.success) {
          // Show success toast
          notification.show({
            title: 'Successfully deleted',
            message: response.message || 'Work order has been removed',
            type: 'success',
            duration: 3000,
            position: 'top-right'
          });

          // Reload requirements list
          const searchQuery = searchCrdId.trim();
          loadRequirements(
            currentPage,
            pageSize,
            searchQuery,
            searchRequirementClientName === 'all' ? 'all' : searchRequirementClientName,
            searchStatus,
            searchStartDate,
            searchCompletionDate,
            searchSignOffDate
          );
        } else {
          // Show error toast
          notification.show({
            title: 'Failed to delete requirement',
            message: response.error || 'Please try again later',
            type: 'error',
            duration: 3000,
            position: 'top-right'
          });
        }
      } else if (confirmState.kind === 'ftp-send') {
        const response = await clientService.sendFtpCredentials(confirmState.id as number);

        // Close the dialog first
        setConfirmState(null);

        if (response.success) {
          const message = typeof response.data === 'string' && response.data.trim()
            ? response.data
            : `SFTP credentials have been sent to ${confirmState.label}`;
          notification.show({
            title: 'SFTP Credentials Sent',
            message,
            type: 'success',
            duration: 3000,
            position: 'top-right',
          });
        } else {
          notification.show({
            title: 'Failed to Send SFTP Credentials',
            message: response.error || 'Please try again later',
            type: 'error',
            duration: 3000,
            position: 'top-right',
          });
        }
      } else if (confirmState.kind === 'ftp-reset') {
        const response = await clientService.resetFtpCredentials(confirmState.id as number);

        // Close the dialog first
        setConfirmState(null);

        if (response.success) {
          const message = typeof response.data === 'string' && response.data.trim()
            ? response.data
            : `SFTP credentials have been reset for ${confirmState.label}`;
          notification.show({
            title: 'SFTP Credentials Reset',
            message,
            type: 'success',
            duration: 3000,
            position: 'top-right',
          });
        } else {
          notification.show({
            title: 'Failed to Reset SFTP Credentials',
            message: response.error || 'Please try again later',
            type: 'error',
            duration: 3000,
            position: 'top-right',
          });
        }
      } else if (confirmState.kind === 'user-reset') {
        const response = await userService.forcedResetUser(confirmState.id);

        // Close the dialog first
        setConfirmState(null);

        if (response.success) {
          // Show success toast
          notification.show({
            title: 'Reset Link Sent',
            message: `Password reset link with username has been sent to user ${confirmState.label}`,
            type: 'success',
            duration: 3000,
            position: 'top-right',
          });
          // Refresh user list
          loadUsers(currentPage, pageSize, searchUsername, selectedStatus, selectedRole);
        } else {
          // Show error toast
          notification.show({
            title: 'Failed to Send Reset Link',
            message: response.error || 'Please try later',
            type: 'error',
            duration: 5000,
            position: 'top-right',
          });
        }
      } else if (confirmState.kind === 'protocol-sftp') {
        await sendProtocolSftpDetails(confirmState.id as string, confirmState.label);
        setConfirmState(null);
      }
    } catch (error) {
      console.error('Error deleting', confirmState.kind, error);
      // Close dialog even on error
      setConfirmState(null);
      // Show user-friendly error toast
      notification.show({
        title: 'Failed to delete user',
        message: 'Please try later',
        type: 'error',
        duration: 5000,
        position: 'top-right',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Calculate stats from API response
  const adminStats: AdminStats = {
    totalUsers: userListResponse?.total_count || users.length,
    activeUsers: users.filter(u => u.status === 'active' || u.is_active).length,
    totalJobs: 150,
    completedJobs: 120,
    pendingJobs: 30,
  };

  // All administration submenus are now visible
  const tabs = [
    { id: 'users', label: 'User Management', icon: '👥' },
    { id: 'clients', label: 'Clients', icon: '🏢' },
    { id: 'protocol', label: 'Requirements', icon: '📋' },
    { id: 'forms', label: 'Protocols', icon: '📝' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
  ];

  // Keep activeTab in sync when the URL changes (e.g. browser back/forward or external links)
  useEffect(() => {
    const nextTab = getInitialTabFromPath(location.pathname);
    setActiveTab(prev => (prev === nextTab ? prev : nextTab));
  }, [location.pathname]);

  const renderUsersTab = () => (
    <div className="admin-tab-content">
      {/* Header removed per request */}

      {/* Statistics tiles commented out as requested */}
      {/* <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Users</h3>
          <p className="stat-number">{adminStats.totalUsers}</p>
        </div>
        <div className="stat-card">
          <h3>Active Users</h3>
          <p className="stat-number">{adminStats.activeUsers}</p>
        </div>
        <div className="stat-card">
          <h3>Admins</h3>
          <p className="stat-number">{users.filter(u => u.role?.toLowerCase() === 'admin').length}</p>
        </div>
        <div className="stat-card">
          <h3>Graders</h3>
          <p className="stat-number">{users.filter(u => u.role?.toLowerCase() === 'grader').length}</p>
        </div>
      </div> */}

      {/* Search and Filter Section */}
      <div className="search-filter-section">
        <div className="filter-controls">
          <div className="status-filter-group search-client-name">
            <label htmlFor="searchUsername">Search Name/Username</label>
            <input
              id="searchUsername"
              type="text"
              placeholder="Search by name or username..."
              value={searchUsername}
              onChange={e => setSearchUsername(e.target.value)}
              onKeyPress={handleKeyPress}
              className="search-input"
            />
          </div>

          <div className="status-filter-group">
            <label htmlFor="statusFilter">Status</label>
            <select
              id="statusFilter"
              value={selectedStatus}
              onChange={e => handleStatusChange(e.target.value)}
              className="admin-filter-select"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="role-filter-group">
            <label htmlFor="roleFilter">Role</label>
            <select
              id="roleFilter"
              value={selectedRole}
              onChange={e => handleRoleChange(e.target.value)}
              className="admin-filter-select"
            >
              <option value="all">All</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
            </select>
          </div>

          <div className="admin-search-buttons">
            <button
              className="admin-search-button"
              onClick={handleSearch}
            >
              Search
            </button>
            <button
              className="admin-clear-button"
              onClick={handleClear}
            >
              Clear
            </button>
          </div>
        </div>
        <button
          className="admin-button primary"
          onClick={() => {
            setEditingUser(null);
            setShowAddUserModal(true);
          }}
        >
          <span className="button-icon">+</span>
          New User
        </button>
      </div>

      <div className="users-table-container">
        {loading ? (
          <div className="loading-container">
            <img src={loadingGif} alt="Loading..." className="loading-gif" />
          </div>
        ) : (
        <>
        <table className="users-table">
          <thead>
            <tr>
              <th className="sl-no-header text-right">Sl. No.</th>
              <th className="text-left">Name</th>
              <th className="text-left">Username</th>
              <th className="text-left email-header">Email</th>
              <th className="text-left">Phone</th>
              <th className="text-left">Role</th>
              <th className="text-center last-login-header">Last Login</th>
              <th className="text-center status-header">Status</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#cbd5e0' }}>
                  No Data Found
                </td>
              </tr>
            ) : (
              <>
                {users.map((user, index) => {
                  const hasName = user.first_name || user.last_name || user.middle_name;
                  const displayName = hasName
                    ? formatNameByCountry(
                        user.first_name || '',
                        user.last_name || '',
                        user.middle_name || '',
                        'IND'
                      )
                    : '-';
                  const usernameValue = user.username || '-';
                  const emailValue = user.email || '-';
                  const phoneValue = user.phone ? utils.formatPhoneNumber(user.phone) : '-';
                  const roleValue = user.role
                    ? (user.role.toLowerCase() === 'user'
                        ? 'User(Grader)'
                        : user.role.charAt(0).toUpperCase() + user.role.slice(1).toLowerCase())
                    : '-';
                  const lastLoginValue = user.last_login
                    ? moment(new Date(user.last_login)).format('DD MMM YYYY, hh:mm A')
                    : 'Never';
                  const statusValue = user.is_active ? 'Active' : 'Inactive';

                  return (
                    <tr key={user.id}>
                      <td className="sl-no-header text-right ellipses-td">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td className="name-cell text-left ellipses-td">
                        <AppTooltip content={displayName}>
                          {displayName}
                        </AppTooltip>
                      </td>
                      <td className="username-cell text-left ellipses-td">
                        <AppTooltip content={usernameValue}>
                          <div className="user-info text-left">
                            <span className="username">{usernameValue}</span>
                          </div>
                        </AppTooltip>
                      </td>
                      <td className="email-cell text-left">
                        <AppTooltip content={emailValue}>{emailValue}</AppTooltip>
                      </td>
                      <td className="phone-cell text-left ellipses-td">
                        <AppTooltip content={phoneValue}>{phoneValue}</AppTooltip>
                      </td>
                      <td className={`text-left role-${user.role} ellipses-td`}>
                        <AppTooltip content={roleValue}>{roleValue}</AppTooltip>
                      </td>
                      <td className="last-login-cell text-center">
                        <AppTooltip content={lastLoginValue}>{lastLoginValue}</AppTooltip>
                      </td>
                      <td className="text-center ellipses-td">
                        <AppTooltip content={statusValue}>{statusValue}</AppTooltip>
                      </td>
                      <td className="actions-cell text-center">
                        <AppTooltip content="Edit User">
                          <button
                            className="action-button edit"
                            onClick={() => handleEditUser(user)}
                          >
                            <span className="action-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </span>
                          </button>
                        </AppTooltip>
                        <AppTooltip content="Send Password Reset Link">
                          <button
                            className="action-button reset"
                            onClick={() => handleForcedReset(String(user.id!))}
                          >
                            <span className="action-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                <polyline points="22,6 12,13 2,6" />
                                <path d="M8 8l4 3 4-3" />
                              </svg>
                            </span>
                          </button>
                        </AppTooltip>
                        <AppTooltip content="Delete User">
                          <button
                            className="action-button delete"
                            onClick={() => handleDeleteUser(String(user.id!))}
                          >
                            <span className="action-icon">🗑️</span>
                          </button>
                        </AppTooltip>
                      </td>
                    </tr>
                  );
                })}
              </>
              )}
            </tbody>
          </table>

        {/* Pagination Controls */}
        <Pagination
          currentPage={currentPage}
          totalPages={userListResponse?.total_pages || 1}
          totalCount={userListResponse?.total_count || 0}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
        </>
      )}
      </div>
    </div>
  );

  const renderClientsTab = () => (
    <div className="admin-tab-content">
      {/* Header removed per request */}

      {/* Search and Filter Section */}
      <div className="search-filter-section">
        <div className="filter-controls">
          <div className="status-filter-group search-client-name">
            <label htmlFor="searchClientName">Search Client Name</label>
            <input
              id="searchClientName"
              type="text"
              placeholder="Search by client name"
              value={searchClientName}
              onChange={e => setSearchClientName(e.target.value)}
              onKeyPress={handleClientKeyPress}
              className="search-input"
            />
          </div>

          <div className="status-filter-group">
            <label htmlFor="clientStatusFilter">Status</label>
            <select
              id="clientStatusFilter"
              value={selectedClientStatus}
              onChange={e => setSelectedClientStatus(e.target.value)}
              className="admin-filter-select"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="admin-search-buttons">
            <button
              className="admin-search-button"
              onClick={handleClientSearch}
            >
              Search
            </button>
            <button
              className="admin-clear-button"
              onClick={handleClientClear}
            >
              Clear
            </button>
          </div>
        </div>
        <button
          className="admin-button primary"
          onClick={() => {
            setEditingClient(null);
            setShowAddClientModal(true);
          }}
        >
          <span className="button-icon">+</span>
          New Client
        </button>
      </div>

      <div className="users-table-container">
        {loading ? (
          <div className="loading-container">
            <img src={loadingGif} alt="Loading..." className="loading-gif" />
          </div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th className="sl-no-header">Sl. No.</th>
                <th className="text-left">Client Name</th>
                <th className="text-left">Organization ID</th>
                <th className="text-left email-header">Email</th>
                <th className="text-left">Phone</th>
                <th className="text-center last-login-header">Created</th>
                <th className="text-center status-header">Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#cbd5e0' }}>
                    No Data Found
                  </td>
                </tr>
              ) : (
                <>
                  {clients.map((client, index) => {
                  const clientName = client.name || '-';
                  const orgId = client.org_id || '-';
                  const clientEmail = client.email || '-';
                  const clientPhone = client.phone ? utils.formatPhoneNumber(client.phone) : '-';
                  const createdAt = client.created_at ? moment(client.created_at).format('DD MMM YYYY') : '-';
                  const status = client.is_active ? 'Active' : 'Inactive';

                  return (
                    <tr key={client.client_id || client.id}>
                      <td className="text-noWrap sl-no-header ellipses-td">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td className="username-cell text-left ellipses-td">
                        <AppTooltip content={clientName}>
                          <span className="username">{clientName}</span>
                        </AppTooltip>
                      </td>
                      <td className="username-cell text-left ellipses-td">
                        <AppTooltip content={orgId}>{orgId}</AppTooltip>
                      </td>
                      <td className="email-cell text-left">
                        <AppTooltip content={clientEmail}>{clientEmail}</AppTooltip>
                      </td>
                      <td className="phone-cell text-left ellipses-td">
                        <AppTooltip content={clientPhone}>{clientPhone}</AppTooltip>
                      </td>
                      <td className="last-login-cell text-center">
                        <AppTooltip content={createdAt}>{createdAt}</AppTooltip>
                      </td>
                      <td className="text-center ellipses-td">
                        <AppTooltip content={status}>{status}</AppTooltip>
                      </td>
                      <td className="actions-cell text-center">
                        <AppTooltip content="Edit Client">
                          <button
                            className="action-button edit"
                            onClick={() => handleEditClient(client)}
                          >
                            <span className="action-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </span>
                          </button>
                        </AppTooltip>
                        {SHOW_SEND_FTP_BUTTON && (
                          <AppTooltip content="Send SFTP Credentials">
                            <button
                              className="action-button ftp-send"
                              onClick={() => handleSendFtpCredentials(client)}
                            >
                              <span className="action-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M22 2L11 13" />
                                  <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                                </svg>
                              </span>
                            </button>
                          </AppTooltip>
                        )}
                        <AppTooltip content="Reset SFTP Password">
                          <button
                            className="action-button ftp-reset"
                            onClick={() => handleResetFtpPassword(client)}
                          >
                            <span className="action-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                              </svg>
                            </span>
                          </button>
                        </AppTooltip>
                        <AppTooltip content="Delete Client">
                          <button
                            className="action-button delete"
                            onClick={() => handleDeleteClient(client.client_id || client.id!)}
                          >
                            <span className="action-icon">🗑️</span>
                          </button>
                        </AppTooltip>
                      </td>
                    </tr>
                  );
                  })}
                </>
              )}
            </tbody>
          </table>
        )}

        {/* Pagination Controls */}
        <Pagination
          currentPage={currentPage}
          totalPages={clientListResponse?.total_pages || 1}
          totalCount={clientListResponse?.total_count || 0}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );

  const renderProtocolTab = () => {
    if (showRequirementForm) {
      return (
        <div className="admin-tab-content">
          <RequirementForm
            requirement={editingRequirementForm}
            onBack={() => {
              setShowRequirementForm(false);
              setEditingRequirementForm(null);
            }}
            onRequirementAdded={newRequirement => {
              // Reload requirements list from server to get fresh data
              const searchQuery = searchCrdId.trim();
              loadRequirements(
                currentPage,
                pageSize,
                searchQuery,
                searchRequirementClientName === 'all' ? 'all' : searchRequirementClientName,
                searchStatus,
                searchStartDate,
                searchCompletionDate,
                searchSignOffDate
              );
              setShowRequirementForm(false);
              setEditingRequirementForm(null);
            }}
            requirementService={requirementService}
            clients={clientsForDropdown}
          />
        </div>
      );
    }

    return (
      <div className="admin-tab-content">
        {/* Search and Filter Section */}
        <div className="search-filter-section">
          <div className="filter-controls">
            <div className="status-filter-group search-work-order-id">
              <label htmlFor="filterCrdOrWorkOrder">CRD ID / Work Order ID</label>
              <input
                id="filterCrdOrWorkOrder"
                type="text"
                value={searchCrdId}
                onChange={e => setSearchCrdId(e.target.value)}
                className="search-input"
                placeholder="Enter CRD ID or Work Order ID"
              />
            </div>

            <div className="status-filter-group">
              <label htmlFor="filterClientName">Client Name</label>
              <select
                id="filterClientName"
                value={searchRequirementClientName}
                onChange={e => setSearchRequirementClientName(e.target.value)}
                className="admin-filter-select"
              >
                <option value="all">All</option>
                {clientsForDropdown.map(client => (
                  <option key={client.client_id || client.id} value={(client.client_id || client.id)?.toString()}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="status-filter-group">
              <label htmlFor="filterStartDate">Start Date</label>
              <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                <input
                  type="text"
                  value={searchStartDate ? moment(searchStartDate).format('DD MMM YYYY') : ''}
                  placeholder="dd MMM yyyy"
                  readOnly
                  onClick={() => {
                    const picker = document.getElementById('filterStartDatePicker');
                    if (picker && 'showPicker' in picker) {
                      (picker as any).showPicker();
                    }
                  }}
                  style={{
                    background: '#090c29',
                    color: 'white',
                    border: '1px solid #3a3f99',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    paddingRight: '35px',
                    fontSize: '13px',
                    width: '100%',
                    cursor: 'pointer',
                  }}
                />
                <span
                  onClick={() => {
                    const picker = document.getElementById('filterStartDatePicker');
                    if (picker && 'showPicker' in picker) {
                      (picker as any).showPicker();
                    }
                  }}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    color: '#9ca3af',
                    fontSize: '16px',
                  }}
                >
                  📅
                </span>
                <input
                  id="filterStartDatePicker"
                  type="date"
                  value={searchStartDate}
                  max={searchCompletionDate || ''}
                  onChange={e => setSearchStartDate(e.target.value)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            </div>

            <div className="status-filter-group">
              <label htmlFor="filterCompletionDate">Completion Date</label>
              <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                <input
                  type="text"
                  value={searchCompletionDate ? moment(searchCompletionDate).format('DD MMM YYYY') : ''}
                  placeholder="dd MMM yyyy"
                  readOnly
                  onClick={() => {
                    const picker = document.getElementById('filterCompletionDatePicker');
                    if (picker && 'showPicker' in picker) {
                      (picker as any).showPicker();
                    }
                  }}
                  style={{
                    background: '#090c29',
                    color: 'white',
                    border: '1px solid #3a3f99',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    paddingRight: '35px',
                    fontSize: '13px',
                    width: '100%',
                    cursor: 'pointer',
                  }}
                />
                <span
                  onClick={() => {
                    const picker = document.getElementById('filterCompletionDatePicker');
                    if (picker && 'showPicker' in picker) {
                      (picker as any).showPicker();
                    }
                  }}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    color: '#9ca3af',
                    fontSize: '16px',
                  }}
                >
                  📅
                </span>
                <input
                  id="filterCompletionDatePicker"
                  type="date"
                  value={searchCompletionDate}
                  min={searchStartDate || ''}
                  onChange={e => setSearchCompletionDate(e.target.value)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            </div>

            <div className="status-filter-group">
              <label htmlFor="statusFilter">Status</label>
              <select
                id="statusFilter"
                value={searchStatus}
                onChange={e => setSearchStatus(e.target.value)}
                className="admin-filter-select"
              >
                <option value="all">All</option>
                <option value="Approved">Approved</option>
                <option value="Pending">Pending</option>
              </select>
            </div>

            {/* <div className="status-filter-group">
              <label htmlFor="filterSignOffDate">Sign-off Date</label>
              <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                <input
                  type="text"
                  value={searchSignOffDate ? moment(searchSignOffDate).format('DD MMM YYYY') : ''}
                  placeholder="dd MMM yyyy"
                  readOnly
                  onClick={() => {
                    const picker = document.getElementById('filterSignOffDatePicker');
                    if (picker && 'showPicker' in picker) {
                      (picker as any).showPicker();
                    }
                  }}
                  style={{
                    background: '#090c29',
                    color: 'white',
                    border: '1px solid #3a3f99',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    paddingRight: '35px',
                    fontSize: '13px',
                    width: '100%',
                    cursor: 'pointer',
                  }}
                />
                <span
                  onClick={() => {
                    const picker = document.getElementById('filterSignOffDatePicker');
                    if (picker && 'showPicker' in picker) {
                      (picker as any).showPicker();
                    }
                  }}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    color: '#9ca3af',
                    fontSize: '16px',
                  }}
                >
                  📅
                </span>
                <input
                  id="filterSignOffDatePicker"
                  type="date"
                  value={searchSignOffDate}
                  onChange={e => setSearchSignOffDate(e.target.value)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            </div> */}

            <div className="admin-search-buttons">
              <button
                className="admin-search-button"
                onClick={handleRequirementSearch}
              >
                Search
              </button>
              <button
                className="admin-clear-button"
                onClick={handleRequirementClear}
              >
                Clear
              </button>
            </div>
          </div>
          <div className="add-requirement-button-container">
            <button
              className="admin-button primary"
              onClick={() => {
                setEditingRequirementForm(null);
                setShowRequirementForm(true);
              }}
            >
              <span className="button-icon">+</span>
              New Requirement
            </button>
          </div>
        </div>

      <div className="users-table-container">
        {loading ? (
          <div className="loading-container">
            <img src={loadingGif} alt="Loading..." className="loading-gif" />
          </div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th className="sl-no-header">Sl. No.</th>
                <th className="text-noWrap text-left">CRD ID</th>
                <th className="text-noWrap text-left">Work Order ID</th>
                <th className="text-noWrap text-left">Client Name</th>
                <th className="text-noWrap text-center">Start Date</th>
                <th className="text-noWrap text-center">Completion Date</th>
                <th className="text-noWrap text-left">Requirement Title</th>
                <th className="text-noWrap text-center">Status</th>
                <th className="text-noWrap text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {requirements.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#cbd5e0' }}>
                    No Data Found
                  </td>
                </tr>
              ) : (
                requirements.map((requirement, index) => {
                  const statusDisplay = formatRequirementStatus(requirement.status);

                  return (
                    <tr key={requirement.id}>
                      <td className="text-noWrap sl-no-header ellipses-td">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td className="text-noWrap text-left ellipses-td">
                        <CellTooltip
                          tooltip={requirement.crdId}
                        >
                          {requirement.crdId}
                        </CellTooltip>
                      </td>
                      <td className="text-noWrap text-left ellipses-td">
                        <CellTooltip
                          tooltip={requirement.workOrderId}
                        >
                          {requirement.workOrderId}
                        </CellTooltip>
                      </td>
                      <td className="text-noWrap text-left ellipses-td">
                        <CellTooltip
                          tooltip={requirement.clientName}
                        >
                          {requirement.clientName}
                        </CellTooltip>
                      </td>
                      <td className="text-noWrap text-center ellipses-td">
                        <CellTooltip
                          tooltip={requirement.startDate ? moment(requirement.startDate).format('DD MMM YYYY') : '-'}
                        >
                          {requirement.startDate ? moment(requirement.startDate).format('DD MMM YYYY') : '-'}
                        </CellTooltip>
                      </td>
                      <td className="text-noWrap text-center ellipses-td">
                        <CellTooltip
                          tooltip={requirement.completionDate ? moment(requirement.completionDate).format('DD MMM YYYY') : '-'}
                        >
                          {requirement.completionDate ? moment(requirement.completionDate).format('DD MMM YYYY') : '-'}
                        </CellTooltip>
                      </td>
                      <td className="text-noWrap text-left ellipses-td">
                        <CellTooltip
                          tooltip={requirement.requirementTitle}
                        >
                          {requirement.requirementTitle}
                        </CellTooltip>
                      </td>
                      <td className="text-noWrap text-center ellipses-td">
                        <CellTooltip tooltip={statusDisplay}>
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                            {requirement.status && requirement.status.toLowerCase().replace(/_/g, '') === 'pending' && (
                              <Icons.StatusWarning style={{ width: '16px', height: '16px', color: '#fbbf24' }} />
                            )}
                            {requirement.status && requirement.status.toLowerCase().replace(/_/g, '') === 'approved' && (
                              <Icons.StatusSuccess style={{ width: '16px', height: '16px', color: '#10b981' }} />
                            )}
                            {statusDisplay}
                          </span>
                        </CellTooltip>
                      </td>
                      <td className="actions-cell text-center">
                        <AppTooltip content="Edit Requirement">
                          <button
                            className="action-button edit"
                            onClick={() => handleEditRequirement(requirement)}
                          >
                            <span className="action-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </span>
                          </button>
                        </AppTooltip>
                        <AppTooltip content="Delete Requirement">
                          <button
                            className="action-button delete"
                            onClick={() => handleDeleteRequirement(requirement.id.toString())}
                          >
                            <span className="action-icon">🗑️</span>
                          </button>
                        </AppTooltip>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}

        {/* Pagination Controls */}
        <Pagination
          currentPage={currentPage}
          totalPages={requirementListResponse?.total_pages || 1}
          totalCount={requirementListResponse?.total_count || 0}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
    );
  };

  const renderFormsTab = () => {
    return (
    <div className="admin-tab-content">
      {!showProtocolEditor && <></>}

      {/* Filters (only list view) */}
      {!showProtocolEditor && (
        <div className="search-filter-section">
          <div className="filter-controls">
            <div className="status-filter-group searchBox">
              <label htmlFor="searchProtocolQuery">Search Protocol</label>
              <input
                id="searchProtocolQuery"
                type="text"
                placeholder="Search by protocol name or parameter..."
                value={searchProtocolQuery}
                onChange={e => setSearchProtocolQuery(e.target.value)}
                onKeyPress={handleProtocolKeyPress}
                className="search-input"
              />
            </div>

            <div className="status-filter-group">
              <label htmlFor="protocolStatusFilter">Form Status</label>
              <select
                id="protocolStatusFilter"
                value={selectedProtocolStatus}
                onChange={e => setSelectedProtocolStatus(e.target.value)}
                className="admin-filter-select"
              >
                <option value="all">All</option>
                <option value="Published">Published</option>
                <option value="Draft">Draft</option>
                <option value="Aborted">Aborted</option>
              </select>
            </div>

            <div className="status-filter-group">
              <label htmlFor="protocolActiveFilter">Status</label>
              <select
                id="protocolActiveFilter"
                value={selectedProtocolActive}
                onChange={e => setSelectedProtocolActive(e.target.value)}
                className="admin-filter-select"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="status-filter-group">
              <label htmlFor="protocolClientFilter">Client</label>
              <select
                id="protocolClientFilter"
                value={selectedProtocolClient}
                onChange={e => setSelectedProtocolClient(e.target.value)}
                className="admin-filter-select"
              >
                <option value="all">All</option>
                {clientsForDropdown.map(client => (
                  <option key={client.id || client.client_id} value={String(client.id || client.client_id)}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="admin-search-buttons">
              <button
                className="admin-search-button"
                onClick={handleProtocolSearch}

              >
                Search
              </button>
              <button
                className="admin-clear-button"
                onClick={handleProtocolClear}
              >
                Clear
              </button>
            </div>
          </div>
          <button
            className="admin-button primary"
            onClick={() => {
              setEditingProtocol(null);
              setIsDuplicatingProtocol(false);
              setShowProtocolEditor(true);
            }}
          >
            <span className="button-icon">+</span>
            New Protocol
          </button>
        </div>
      )}

      {/* Table or Editor */}
      {!showProtocolEditor && (
        <div className="users-table-container">
          {loading ? (
            <div className="loading-container">
              <img src={loadingGif} alt="Loading..." className="loading-gif" />
            </div>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th className="sl-no-header text-right">Sl. No.</th>
                  <th className="text-left">Name</th>
                  <th className="text-center">Protocol Version</th>
                  <th className="text-left">Client</th>
                  <th className="text-left">Parameters</th>
                  <th className="text-center">Form Status</th>
                  <th className="text-center">Protocol Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {protocols.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#cbd5e0' }}>
                      No Data Found
                    </td>
                  </tr>
                ) : (
                <>
                {protocols.map((p, index) => {
                  // Collect all parameter names from the nested structure
                  const collectParamNames = (nodes: any[]): string[] =>
                    nodes.flatMap(n => [
                      n.parameter_name,
                      ...(n.children ? collectParamNames(n.children) : []),
                    ]);
                  const allParamNames = collectParamNames(p.parameters || []);
                  const protocolName = p.name || '-';
                  const protocolVersion = (p as any).version ?? '-';
                  // Derive client name based on client id present on the protocol
                  const protocolClientId: number | null =
                    (p as any).client_id ??
                    (p as any).clientId ??
                    null;
                  const clientName =
                    protocolClientId != null
                      ? (clientsForDropdown.find(c => {
                          const clientIdValue =
                            (c as any).client_id ?? c.id;
                          return Number(clientIdValue) === Number(protocolClientId);
                        })?.name || '-')
                      : '-';
                  const parametersPreview = allParamNames.slice(0, 2);
                  const formStatus =
                    typeof p.status === 'string' && p.status.length > 0
                      ? p.status.charAt(0).toUpperCase() + p.status.slice(1).toLowerCase()
                      : '-';
                  const protocolStatus = p.is_active ? 'Active' : 'Inactive';
                  const protocolIdString = p?.id ? String(p.id) : '';

                  const isPublished =
                    typeof p.status === 'string' &&
                    p.status.toLowerCase() === 'published';
                  const isAborted =
                    typeof p.status === 'string' &&
                    (p.status.toLowerCase() === 'abort' || p.status.toLowerCase() === 'aborted');

                  return (
                    <tr key={p.id}>
                      <td className="sl-no-header text-right ellipses-td">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td className="text-left ellipses-td">
                        <AppTooltip content={protocolName}>{protocolName}</AppTooltip>
                      </td>
                      <td className="text-center ellipses-td">
                        <AppTooltip content={protocolVersion.toString()}>{protocolVersion}</AppTooltip>
                      </td>
                      <td className="text-left ellipses-td">
                        <AppTooltip content={clientName}>{clientName}</AppTooltip>
                      </td>
                      <td className="parameters parameters-header ellipses-td">
                        <div className="parameters-wrapper vertical">
                          {parametersPreview.map((param, idx) => (
                            <div
                              key={param}
                              className="parameter-row"
                            >
                              <div className="parameter-tag">{param}</div>
                              {idx === 1 && allParamNames.length > 2 && (
                                <span className="parameter-count-badge">
                                  +{allParamNames.length - 2}
                                </span>
                              )}
                            </div>
                              ))}
                          <div className="parameters-popover">
                            <div className="popover-title">
                              PARAMETERS ({allParamNames.length})
                            </div>
                            <div className="popover-list">
                              {allParamNames.map((param, paramIdx) => (
                                <div
                                  key={`popover-${protocolIdString}-${param}-${paramIdx}`}
                                  className="parameter-popover-item"
                                >
                                  <span className="parameter-popover-dot"></span>
                                  <span className="parameter-popover-text">{param}</span>
                          </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="text-center ellipses-td">
                        <AppTooltip content={formStatus}>
                          <span className="status-with-icon">
                            <span className="status-icon" aria-hidden="true">
                              {p.status && p.status.toLowerCase() === 'published' ? (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#348cfd"/>
                                  <path d="M14 2v6h6" stroke="#5acce6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                                  <path d="M9 15l2 2 4-4" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                                </svg>
                              ) : p.status && (p.status.toLowerCase() === 'abort' || p.status.toLowerCase() === 'aborted') ? '🚫' : '✏️'}
                            </span>
                            <span>{formStatus}</span>
                          </span>
                        </AppTooltip>
                      </td>
                      <td className="text-center ellipses-td">
                        <AppTooltip content={protocolStatus}>
                          <span className="status-with-icon">
                            <span className="status-icon" aria-hidden="true">
                              {p.is_active ? '✅' : '🕓'}
                            </span>
                            <span>{protocolStatus}</span>
                          </span>
                        </AppTooltip>
                      </td>
                      <td className="actions-cell text-center">
                        {isPublished || isAborted ? (
                          <AppTooltip content="View Protocol">
                            <button
                              className="action-button edit"
                              onClick={() => {
                                setEditingProtocol(p);
                                setIsDuplicatingProtocol(false);
                                setShowProtocolEditor(true);
                              }}
                            >
                              <span className="action-icon">👁️</span>
                            </button>
                          </AppTooltip>
                        ) : (
                          <AppTooltip content="Edit Protocol">
                            <button
                              className="action-button edit"
                              onClick={() => {
                                setEditingProtocol(p);
                                setIsDuplicatingProtocol(false);
                                setShowProtocolEditor(true);
                              }}
                            >
                              <span className="action-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                              </span>
                            </button>
                          </AppTooltip>
                        )}
                        {isPublished && p.status?.toLowerCase() !== 'abort' && p.status?.toLowerCase() !== 'aborted' && (
                          <AppTooltip content="Duplicate Protocol">
                            <button
                              className="action-button edit"
                              onClick={() => {
                                setEditingProtocol(p);
                                setIsDuplicatingProtocol(true);
                                setShowProtocolEditor(true);
                              }}
                            >
                              <span className="action-icon">⧉</span>
                            </button>
                          </AppTooltip>
                        )}
                        {isPublished && p.status?.toLowerCase() !== 'abort' && p.status?.toLowerCase() !== 'aborted' && (
                          <AppTooltip content="Send SFTP Path & Credentials">
                            <button
                              className="action-button ftp-send"
                              type="button"
                              onClick={() => promptSendProtocolSftpDetails(p)}
                              disabled={!protocolIdString || sendingProtocolId === protocolIdString}
                              aria-busy={sendingProtocolId === protocolIdString}
                            >
                              <span className="action-icon" style={{ opacity: sendingProtocolId === protocolIdString ? 0.6 : 1 }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M22 2L11 13" />
                                  <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                                </svg>
                              </span>
                            </button>
                          </AppTooltip>
                        )}
                        <AppTooltip content="Print Protocol">
                          <button
                            className="action-button print-protocol"
                            type="button"
                            aria-label="Print Protocol"
                            onClick={() => handlePrintProtocol(p)}
                            disabled={!protocolIdString || printingProtocolId === protocolIdString}
                            aria-busy={printingProtocolId === protocolIdString}
                          >
                            <span
                              className="pdf-icon-colorful"
                              style={{ opacity: printingProtocolId === protocolIdString ? 0.6 : 1 }}
                            >
                              <FontAwesomeIcon icon={faFilePdf} className="pdf-fa-icon" />
                            </span>
                          </button>
                        </AppTooltip>
                        <AppTooltip content="Delete Protocol">
                          <button
                            className="action-button delete"
                            onClick={() => handleDeleteProtocol(p.id)}
                          >
                            <span className="action-icon">🗑️</span>
                          </button>
                        </AppTooltip>
                      </td>
                    </tr>
                  );
                })}
                </>
                )}
              </tbody>
            </table>
          )}

          {/* Pagination Controls */}
          <Pagination
            currentPage={currentPage}
            totalPages={protocolListResponse?.total_pages || Math.max(1, Math.ceil((protocolListResponse?.total_count || protocolListResponse?.total || 0) / pageSize))}
            totalCount={protocolListResponse?.total_count || protocolListResponse?.total || 0}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {showProtocolEditor && (
        <ProtocolEditor
          protocol={editingProtocol}
          isDuplicating={isDuplicatingProtocol}
          onBack={() => setShowProtocolEditor(false)}
          onSave={() => {
            loadProtocols(
              currentPage,
              pageSize,
              searchProtocolQuery || null,
              selectedProtocolStatus,
              selectedProtocolActive,
              searchProtocolQuery,
              selectedProtocolClient
            );
          }}
          crdDocs={availableCrdDocs}
          managers={availableManagers}
        />
      )}
    </div>
  );
  };

  const renderSettingsTab = () => (
    <div className="admin-tab-content">
      <h2>System Settings</h2>
      <div className="admin-section">
        <h3>Application Settings</h3>
        <div className="settings-group">
          <div className="setting-item">
            <label>System Name</label>
            <input
              type="text"
              defaultValue="Grading Job Management System"
            />
          </div>
          <div className="setting-item">
            <label>Default Job Priority</label>
            <select defaultValue="medium">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div className="setting-item">
            <label>Auto-assign Jobs</label>
            <input
              type="checkbox"
              defaultChecked
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'users':
        return renderUsersTab();
      case 'clients':
        return renderClientsTab();
      case 'protocol':
        return renderProtocolTab();
      case 'forms':
        return renderFormsTab();
      case 'settings':
        return renderSettingsTab();
      default:
        return renderUsersTab();
    }
  };

  // Check if user is admin (case-insensitive)
  if (user?.role?.toLowerCase() !== 'admin') {
    return (
      <div className="main-container">
        <AppHeader />
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You need administrator privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container">
      <AppHeader />

      <div className="admin-content">
        {confirmState && (
          <div
            className="modal-overlay top-center"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-content">
              {/* Loading overlay */}
              {isDeleting && (
                <div className="modal-loading-overlay">
                  <img src={loadingGif} alt="Loading..." className="loading-gif" />
                </div>
              )}

              <div className="modal-header">
                <h3>
                  {confirmState.kind === 'ftp-send'
                    ? 'Confirm Send SFTP Credentials'
                    : confirmState.kind === 'ftp-reset'
                    ? 'Confirm Reset SFTP Password'
                    : confirmState.kind === 'protocol-sftp'
                    ? 'Confirm Send SFTP Details'
                    : confirmState.kind === 'user-reset'
                    ? 'Send Password Reset Link'
                    : 'Confirm Delete'}
                </h3>
                <button
                  className="admin-button secondary close"
                  onClick={() => setConfirmState(null)}
                  disabled={isDeleting}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                {confirmState.kind === 'requirement' ? (
                  <div>
                    <p>
                      You are about to delete the requirement <strong>"{confirmState.requirementTitle}"</strong> for client <strong>"{confirmState.clientName}"</strong>.
                    </p>
                    <p style={{ marginTop: '12px' }}>
                      Once deleted, all associated protocols and study files will no longer be accessible.
                    </p>
                    <p style={{ marginTop: '12px', fontWeight: '600' }}>
                      Do you wish to continue?
                    </p>
                  </div>
                ) : confirmState.kind === 'ftp-send' ? (
                  <p>
                    Are you sure you want to send SFTP credentials to client{' '}
                    <strong>{confirmState.label}</strong>?
                  </p>
                ) : confirmState.kind === 'ftp-reset' ? (
                  <p>
                    Are you sure you want to reset the SFTP password for client{' '}
                    <strong>{confirmState.label}</strong>?
                  </p>
                ) : confirmState.kind === 'protocol-sftp' ? (
                  <p>
                    Are you sure you want to send the SFTP path and credentials for protocol{' '}
                    <strong>{confirmState.label}</strong>?
                  </p>
                ) : confirmState.kind === 'user-reset' ? (
                  <p>
                    Are you sure you want to send a password reset link to user{' '}
                    <strong>{confirmState.label}</strong>?
                  </p>
                ) : (
                  <p>
                    Are you sure you want to delete the {confirmState.kind === 'user' ? 'user' : confirmState.kind}{' '}
                    <strong>{confirmState.label}</strong>?
                  </p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="admin-button secondary"
                  onClick={() => setConfirmState(null)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="admin-button primary"
                  onClick={confirmDeletion}
                  disabled={isDeleting}
                >
                  {isDeleting
                    ? confirmState.kind === 'ftp-send' || confirmState.kind === 'protocol-sftp' || confirmState.kind === 'user-reset'
                      ? 'Sending...'
                      : confirmState.kind === 'ftp-reset'
                      ? 'Resetting...'
                      : 'Deleting...'
                    : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="admin-tabs">
          {tabs.map(tab => {
            const handleClick = () => {
              if (tab.id === 'settings') {
                return;
              }

              let segment: string;
              switch (tab.id) {
                case 'clients':
                  segment = 'clients';
                  break;
                case 'protocol':
                  segment = 'requirements';
                  break;
                case 'forms':
                  segment = 'protocols';
                  break;
                case 'users':
                default:
                  segment = 'users';
                  break;
              }

              setActiveTab(tab.id as any);
              navigate(`/admin/${segment}`, { replace: true });
            };

            return (
              <button
                key={tab.id}
                className={`admin-tab ${activeTab === tab.id ? 'active' : ''} ${tab.id === 'settings' ? 'disabled' : ''}`}
                onClick={handleClick}
                style={tab.id === 'settings' ? { cursor: 'not-allowed' } : {}}
              >
                {/* <span className="tab-icon">{tab.icon}</span> */}
                <span className="tab-label">{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div className="admin-main">{renderTabContent()}</div>
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <AddUserModal
          onClose={() => {
            setShowAddUserModal(false);
            setEditingUser(null);
          }}
          onUserAdded={newUser => {
            setShowAddUserModal(false);
            setEditingUser(null);
            // Refresh user list from API to get updated data
            loadUsers(currentPage, pageSize, searchUsername, selectedStatus, selectedRole);
          }}
          userService={userService}
          editingUser={editingUser}
        />
      )}

      {/* Add Client Modal */}
      {showAddClientModal && (
        <AddClientModal
          onClose={() => {
            setShowAddClientModal(false);
            setEditingClient(null);
          }}
          onClientAdded={newClient => {
            // Refresh the client list after add/edit
            loadClients(currentPage, 5, searchClientName, selectedClientStatus);
            setShowAddClientModal(false);
            setEditingClient(null);
          }}
          clientService={clientService}
          editingClient={editingClient}
        />
      )}

    </div>
  );
};

// Add User Modal Component
interface AddUserModalProps {
  onClose: () => void;
  onUserAdded: (user: User) => void;
  userService: UserService;
  editingUser?: User | null;
}

const AddUserModal: React.FC<AddUserModalProps> = ({
  onClose,
  onUserAdded,
  userService,
  editingUser,
}) => {
  const notification = useNotification();
  // Extract country code and phone number from editing user
  const extractCountryCode = (phone: string | null | undefined): string => {
    if (!phone) return DEFAULT_COUNTRY_CODE;
    const match = phone.match(/^(\+\d+)\s/);
    return match ? match[1] : DEFAULT_COUNTRY_CODE;
  };

  const extractPhoneNumber = (phone: string | null | undefined): string => {
    if (!phone) return '';
    const match = phone.match(/^\+\d+\s(.+)$/);
    return match ? match[1] : phone;
  };

  const [formData, setFormData] = useState<CreateUserRequest>({
    username: editingUser?.username || '',
    password: '',
    email: editingUser?.email || '',
    role: (editingUser?.role?.toLowerCase() as 'user' | 'admin' | 'manager') || 'user',
    status: editingUser?.is_active !== undefined ? (editingUser.is_active ? 'active' : 'inactive') : 'active',
    specialization: editingUser?.specialization || '',
    phone: extractPhoneNumber(editingUser?.phone),
    affiliation: editingUser?.affiliation || '',
  });

  // Derived name fields for UI (first/middle/last)
  const [firstName, setFirstName] = useState<string>(
    editingUser?.first_name || ''
  );
  const [middleName, setMiddleName] = useState<string>(
    editingUser?.middle_name || ''
  );
  const [lastName, setLastName] = useState<string>(
    editingUser?.last_name || ''
  );

  const [errors, setErrors] = useState<Partial<CreateUserRequest>>({});
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState(extractCountryCode(editingUser?.phone));

  const validateForm = (): boolean => {
    const newErrors: Partial<CreateUserRequest> = {};

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    // Password validation removed - password is auto-generated by backend and sent via email

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // First/Last name validation (required)
    if (!firstName.trim()) {
      // use API field keys for consistency
      // @ts-ignore - using CreateUserRequest compatible keys
      newErrors.first_name = 'First name is required';
    }
    if (!lastName.trim()) {
      // @ts-ignore - using CreateUserRequest compatible keys
      newErrors.last_name = 'Last name is required';
    }

    // Phone validation
    const trimmedPhone = (formData.phone || '').trim();
    if (trimmedPhone) {
      if (trimmedPhone.length !== 10) {
        newErrors.phone = 'Phone number must be exactly 10 digits';
      } else if (!/^\d+$/.test(trimmedPhone)) {
        newErrors.phone = 'Phone number must contain only digits';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Prepare payload according to API specification
      const userPayload: CreateUserRequest = {
        first_name: firstName.trim() || null,
        middle_name: middleName.trim() || null,
        last_name: lastName.trim() || null,
        username: formData.username,
        // Password is auto-generated by backend and sent via email for both new and edited users
        password: '',
        email: formData.email,
        role: formData.role,
        phone: formData.phone ? `${countryCode} ${formData.phone.trim()}` : null,
        specialization: formData.specialization || null,
        is_active: formData.status === 'active' ? true:  false,
      };

      // For edit operations, include user_id
      if (editingUser && editingUser.id) {
        userPayload.user_id = typeof editingUser.id === 'number' ? editingUser.id : parseInt(editingUser.id, 10);
      }

      const response = await userService.createUser(userPayload);

      if (response.success && response.data) {
        // Show success toast
        notification.show({
          title: editingUser ? 'Successfully updated' : 'Successfully created',
          message: editingUser ? 'User has been updated' : 'User has been added',
          type: 'success',
          duration: 3000,
          position: 'top-right',
        });

        onUserAdded(response.data);
      } else {
        // Show error toast with message from API response
        notification.show({
          title: editingUser ? 'Failed to update user' : 'Failed to create user',
          message: response.error || 'Please try later',
          type: 'error',
          duration: 5000,
          position: 'top-right',
        });
        // Don't close the modal - user can correct the error
      }
    } catch (error) {
      console.error(`Error ${editingUser ? 'updating' : 'creating'} user:`, error);

      // Show error toast
      notification.show({
        title: editingUser ? 'Failed to update user' : 'Failed to create user',
        message: 'Please try later',
        type: 'error',
        duration: 5000,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CreateUserRequest, value: string) => {
    let nextValue = value;

    if (field === 'phone') {
      nextValue = value.replace(/\D/g, '').slice(0, 10);
    }

    setFormData(prev => ({ ...prev, [field]: nextValue }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        {/* Loading overlay */}
        {loading && (
          <div className="modal-loading-overlay">
            <img src={loadingGif} alt="Loading..." className="loading-gif" />
          </div>
        )}

        <div className="modal-header">
          <h3>{editingUser ? 'Edit User' : 'Add New User'}</h3>
          <button
            className="admin-button secondary close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="modal-body"
          autoComplete="off"
          data-form-type="other"
        >
          {/* Hidden dummy fields to prevent browser autofill */}
          <input type="text" name="fake-username" autoComplete="off" style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} tabIndex={-1} readOnly />
          <input type="password" name="fake-password" autoComplete="off" style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} tabIndex={-1} readOnly />

          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                className={errors.first_name ? 'error' : ''}
                placeholder="Enter first name"
                maxLength={100}
              />
              {errors.first_name && <div className="error-message">{errors.first_name as any}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="middleName">Middle Name</label>
              <input
                type="text"
                id="middleName"
                value={middleName}
                onChange={e => setMiddleName(e.target.value)}
                placeholder="Enter middle name"
                maxLength={100}
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                className={errors.last_name ? 'error' : ''}
                placeholder="Enter last name"
                maxLength={100}
              />
              {errors.last_name && <div className="error-message">{errors.last_name as any}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="username">Username *</label>
              <input
                type="text"
                id="username"
                name="new-username-field"
                value={formData.username}
                onChange={e => handleInputChange('username', e.target.value)}
                className={errors.username ? 'error' : ''}
                placeholder="Enter username"
                maxLength={50}
                autoComplete="off"
                data-form-type="other"
                data-lpignore="true"
                data-1p-ignore="true"
              />
              {errors.username && <div className="error-message">{errors.username}</div>}
            </div>


            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={e => handleInputChange('email', e.target.value)}
                className={errors.email ? 'error' : ''}
                placeholder="Enter email address"
                maxLength={100}
              />
              {errors.email && <div className="error-message">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="role">Role *</label>
              <select
                id="role"
                value={formData.role}
                onChange={e => handleInputChange('role', e.target.value as any)}
              >
                <option value="user">User(Grader)</option>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status">Status *</label>
              <select
                id="status"
                value={formData.status}
                onChange={e => handleInputChange('status', e.target.value as any)}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="specialization">Specialization</label>
              <input
                type="text"
                id="specialization"
                value={formData.specialization}
                onChange={e => handleInputChange('specialization', e.target.value)}
                placeholder="Enter specialization"
                maxLength={100}
              />
            </div>

            <div className="form-group phone-group">
              <label htmlFor="phone">Phone Number</label>
              <div className="phone-input-container">
                <select
                  value={countryCode}
                  onChange={e => setCountryCode(e.target.value)}
                  className="country-code-select"
                >
                  {COUNTRY_CODES.map(country => (
                    <option key={country.value} value={country.value}>
                      {country.value} ({country.label})
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={e => handleInputChange('phone', e.target.value)}
                  className={errors.phone ? 'error' : ''}
                  placeholder="Enter 10-digit phone number"
                  maxLength={10}
                  inputMode="numeric"
                />
              </div>
              {errors.phone && <div className="error-message">{errors.phone}</div>}
            </div>
          </div>
        </form>

        <div className="modal-footer">
          <button
            type="button"
            className="admin-button secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="admin-button primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading
              ? 'Saving...'
              : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Add Client Modal Component
interface AddClientModalProps {
  onClose: () => void;
  onClientAdded: (client: Client) => void;
  clientService: ClientService;
  editingClient?: Client | null;
}

const AddClientModal: React.FC<AddClientModalProps> = ({
  onClose,
  onClientAdded,
  clientService,
  editingClient,
}) => {
  const notification = useNotification();
  // Parse existing phone number for edit
  const parseExistingPhone = (phone: string | undefined) => {
    if (!phone) return { countryCode: DEFAULT_COUNTRY_CODE, phoneNumber: '' };
    const match = phone.match(/^(\+\d+)\s(.+)$/);
    if (match) {
      return {
        countryCode: match[1],
        phoneNumber: match[2].replace(/\D/g, '').slice(0, 10),
      };
    }
    return {
      countryCode: DEFAULT_COUNTRY_CODE,
      phoneNumber: phone.replace(/\D/g, '').slice(0, 10),
    };
  };

  const [phoneData, setPhoneData] = useState(() => parseExistingPhone(editingClient?.phone));

  const [formData, setFormData] = useState<CreateClientRequest>({
    org_id: editingClient?.org_id || '',
    name: editingClient?.name || '',
    email: editingClient?.email || '',
    phone: editingClient?.phone || '',
    is_active: editingClient?.is_active ?? true,
  });

  const [errors, setErrors] = useState<Partial<CreateClientRequest>>({});
  const [loading, setLoading] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Partial<CreateClientRequest> = {};

    // Organisation Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Organisation Name is required';
    }

    // Contact Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Contact Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Contact Phone validation
    if (!phoneData.phoneNumber.trim()) {
      newErrors.phone = 'Contact Phone number is required';
    } else if (phoneData.phoneNumber.length !== 10) {
      newErrors.phone = 'Contact Phone number must be exactly 10 digits';
    } else if (!/^\d+$/.test(phoneData.phoneNumber)) {
      newErrors.phone = 'Contact Phone number must contain only digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Combine country code and phone number
      const fullPhoneNumber = `${phoneData.countryCode} ${phoneData.phoneNumber}`;

      let response;
      const clientData = {
        ...formData,
        phone: fullPhoneNumber,
      };

      if (editingClient) {
        // Update existing client
        const updateData: UpdateClientRequest = {
          ...clientData,
          client_id: editingClient.client_id || editingClient.id!,
        };
        response = await clientService.updateClient(editingClient.client_id || editingClient.id!, updateData);
      } else {
        // Create new client
        response = await clientService.createClient(clientData);
      }

      if (response.success && response.data) {
        onClientAdded(response.data);
        notification.show({
          title: 'Success',
          message: `Client ${editingClient ? 'updated' : 'created'} successfully`,
          type: 'success',
          position: 'top-right',
        });
      } else {
        notification.show({
          title: 'Error',
          message: `Failed to ${editingClient ? 'update' : 'create'} client: ${response.error || 'Unknown error'}`,
          type: 'error',
          position: 'top-right',
        });
      }
    } catch (error) {
      console.error(`Error ${editingClient ? 'updating' : 'creating'} client:`, error);
      notification.show({
        title: 'Error',
        message: `An error occurred while ${editingClient ? 'updating' : 'creating'} the client`,
        type: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CreateClientRequest, value: string | boolean | number | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handlePhoneChange = (field: 'countryCode' | 'phoneNumber', value: string) => {
    const nextValue =
      field === 'phoneNumber' ? value.replace(/\D/g, '').slice(0, 10) : value;
    setPhoneData(prev => ({ ...prev, [field]: nextValue }));
    // Clear phone error when user starts typing
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: undefined }));
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{editingClient ? 'Edit Client' : 'Add New Client'}</h3>
          <button
            className="admin-button secondary close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="modal-body"
        >
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="clientName">Organisation Name *</label>
              <input
                type="text"
                id="clientName"
                value={formData.name}
                onChange={e => handleInputChange('name', e.target.value)}
                className={errors.name ? 'error' : ''}
                placeholder="Enter organisation name"
                maxLength={100}
              />
              {errors.name && <div className="error-message">{errors.name}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="organizationId">Organization ID</label>
              <input
                type="text"
                id="organizationId"
                value={formData.org_id || ''}
                onChange={e => handleInputChange('org_id', e.target.value)}
                className={errors.org_id ? 'error' : ''}
                placeholder="Enter organization ID (optional)"
                maxLength={50}
              />
              {errors.org_id && <div className="error-message">{errors.org_id}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="clientEmail">Contact Email *</label>
              <input
                type="email"
                id="clientEmail"
                value={formData.email}
                onChange={e => handleInputChange('email', e.target.value)}
                className={errors.email ? 'error' : ''}
                placeholder="Enter contact email address"
                maxLength={100}
              />
              {errors.email && <div className="error-message">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="clientPhone">Contact Phone *</label>
              <div className="phone-input-container">
                <select
                  id="clientCountryCode"
                  value={phoneData.countryCode}
                  onChange={e => handlePhoneChange('countryCode', e.target.value)}
                  className={`country-code-select ${errors.phone ? 'error' : ''}`}
                >
                  {COUNTRY_CODES.map(country => (
                    <option key={country.value} value={country.value}>
                      {country.value} ({country.label})
                    </option>
                  ))}

                </select>
                <input
                  type="tel"
                  id="clientPhone"
                  value={phoneData.phoneNumber}
                  onChange={e => handlePhoneChange('phoneNumber', e.target.value)}
                  className={`${errors.phone ? 'error' : ''}`}
                  placeholder="Enter 10-digit phone number"
                  maxLength={10}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
              {errors.phone && <div className="error-message">{errors.phone}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="clientStatus">Status *</label>
              <select
                id="clientStatus"
                value={formData.is_active ? 'active' : 'inactive'}
                onChange={e => handleInputChange('is_active', e.target.value === 'active')}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </form>

        <div className="modal-footer">
          <button
            type="button"
            className="admin-button secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="admin-button primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Requirement Form Component (Inline)
interface RequirementFormProps {
  onBack: () => void;
  onRequirementAdded: (requirement: Requirement) => void;
  requirementService: RequirementService;
  requirement?: Requirement | null;
  clients: Client[];
}

const RequirementForm: React.FC<RequirementFormProps> = ({
  onBack,
  onRequirementAdded,
  requirementService,
  requirement,
  clients,
}) => {
  const notification = useNotification();

  // Helper function to convert ISO date to YYYY-MM-DD format for date inputs
  const formatDateForInput = (isoDate: string | undefined | null): string => {
    if (!isoDate) return '';
    try {
      // Extract YYYY-MM-DD from ISO format (e.g., "2025-09-09T16:40:29" -> "2025-09-09")
      return isoDate.split('T')[0];
    } catch {
      return '';
    }
  };

  // Helper function to convert API status to form status (uppercase to title case)
  const normalizeWorkOrderStatus = (status?: string): 'Initiated' | 'In Progress' | 'Completed' | 'Cancelled' => {
    if (!status) return 'Initiated';
    const upperStatus = status.toUpperCase().replace(/_/g, ' '); // Handle both "IN_PROGRESS" and "IN PROGRESS"
    if (upperStatus === 'INITIATED') return 'Initiated';
    if (upperStatus === 'IN PROGRESS') return 'In Progress';
    if (upperStatus === 'COMPLETED') return 'Completed';
    if (upperStatus === 'CANCELLED') return 'Cancelled';
    // If no match, return as-is with proper capitalization
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() as any;
  };

  // Helper function to convert API status to CRD status
  const normalizeCRDStatus = (status?: string): 'Approved' | 'Pending' | 'Rejected' => {
    if (!status) return 'Approved';
    const upperStatus = status.toUpperCase();
    if (upperStatus === 'APPROVED') return 'Approved';
    if (upperStatus === 'PENDING') return 'Pending';
    if (upperStatus === 'REJECTED') return 'Rejected';
    return status as any;
  };

  const [workOrder, setWorkOrder] = useState<WorkOrder>({
    workOrderId: requirement?.workOrderId || '',
    clientName: requirement?.client_id?.toString() || '',  // Use client_id instead of clientName
    mouId: requirement?.mouId || '',
    studyId: requirement?.studyId || '',
    studyTitle: requirement?.studyTitle || '',
    startDate: formatDateForInput(requirement?.startDate),
    expectedCompletionDate: formatDateForInput(requirement?.completionDate),
    status: normalizeWorkOrderStatus(requirement?.work_order_status || requirement?.status), // Use work_order_status first
  });

  const [crd, setCrd] = useState<ClientRequirementDocument>({
    crdId: requirement?.crdId || '',
    requirementTitle: requirement?.requirementTitle || '',
    imagingModalities: requirement?.imagingModalities || [],
    fileFormat: (Array.isArray(requirement?.fileFormat) ? requirement?.fileFormat : (requirement?.fileFormat ? [requirement?.fileFormat] : [])) as string[],
    numberOfFiles: requirement?.numberOfFiles,
    annotationRequirements: requirement?.annotationRequirements || '',
    reportingFormat: requirement?.reportingFormat || '',
    signedOffBy: requirement?.signedOffBy || '',
    signOffDate: formatDateForInput(requirement?.signOffDate),
    status: normalizeCRDStatus(requirement?.requirement_doc?.status),
    documentUpload: requirement?.documentUpload || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // Initialize uploadedFileData with existing file_data when editing
  const [uploadedFileData, setUploadedFileData] = useState<any>(requirement?.file_data || null);
  const [isUploading, setIsUploading] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Work Order validations
    if (!workOrder.workOrderId.trim()) {
      newErrors.workOrderId = 'Work Order ID is required';
    }
    if (!workOrder.clientName.trim()) {
      newErrors.clientName = 'Client Name is required';
    }
    if (!workOrder.mouId.trim()) {
      newErrors.mouId = 'MoU ID is required';
    }
    if (!workOrder.studyId.trim()) {
      newErrors.studyId = 'Study ID is required';
    }
    if (!workOrder.studyTitle.trim()) {
      newErrors.studyTitle = 'Study Title is required';
    }
    if (!workOrder.startDate) {
      newErrors.startDate = 'Start Date is required';
    }
    if (!workOrder.expectedCompletionDate) {
      newErrors.expectedCompletionDate = 'Expected Completion Date is required';
    }
    if (!workOrder.status) {
      newErrors.woStatus = 'Work Order Status is required';
    }

    // CRD validations
    if (!crd.crdId.trim()) {
      newErrors.crdId = 'CRD ID is required';
    }
    if (!crd.requirementTitle.trim()) {
      newErrors.requirementTitle = 'Requirement Title is required';
    }
    if (!crd.imagingModalities || crd.imagingModalities.length === 0) {
      newErrors.imagingModalities = 'Imaging Modalities is required';
    }
    if (!crd.fileFormat || crd.fileFormat.length === 0) {
      newErrors.fileFormat = 'At least one File Format is required';
    }
    if (crd.numberOfFiles === undefined || crd.numberOfFiles === null || Number.isNaN(crd.numberOfFiles as any)) {
      newErrors.numberOfFiles = 'Number of Files is required';
    } else if (crd.numberOfFiles <= 0) {
      newErrors.numberOfFiles = 'Number of Files must be greater than 0';
    }
    if (!crd.annotationRequirements.trim()) {
      newErrors.annotationRequirements = 'Annotation Requirements is required';
    }
    if (!crd.reportingFormat.trim()) {
      newErrors.reportingFormat = 'Reporting Format is required';
    }
    if (!crd.signedOffBy.trim()) {
      newErrors.signedOffBy = 'Signed Off By is required';
    }
    if (!crd.signOffDate) {
      newErrors.signOffDate = 'Sign-off Date is required';
    }
    if (!crd.status) {
      newErrors.crdStatus = 'CRD Status is required';
    }
    if (!crd.documentUpload.trim()) {
      newErrors.documentUpload = 'Document Upload is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    // Clear any previous upload data
    setUploadedFileData(null);

    try {
      const apiService = ApiService.getInstance();

      // Get signed URL response - we'll use this data for file_data payload
      const signedUrlResponse = await apiService.getSignedUploadUrl(
        file.name,
        file.type,
        'PATIENT_001',
        'documents'
      );

      if (!signedUrlResponse.success || !signedUrlResponse.data || !signedUrlResponse.data.is_success) {
        throw new Error('Failed to get file upload information');
      }

      // Access the response data - res.data contains signed_url_info and file_info
      const res = signedUrlResponse.data;
      const signedUrlInfo = res.data?.signed_url_info;
      const fileInfo = res.data?.file_info;

      if (!signedUrlInfo || !signedUrlInfo.url || !signedUrlInfo.fields) {
        throw new Error('Invalid signed URL response structure');
      }

      // Create FormData for S3 upload
      const formData = new FormData();
      formData.append('Content-Type', signedUrlInfo.fields['Content-Type'] || file.type);
      formData.append('key', signedUrlInfo.fields['key']);
      formData.append('policy', signedUrlInfo.fields['policy']);
      formData.append('X-Amz-Algorithm', signedUrlInfo.fields['x-amz-algorithm']);
      formData.append('X-Amz-Credential', signedUrlInfo.fields['x-amz-credential']);
      formData.append('X-Amz-Date', signedUrlInfo.fields['x-amz-date']);
      formData.append('X-Amz-Signature', signedUrlInfo.fields['x-amz-signature']);
      formData.append('file', file, file.name);

      // Upload file to S3 using the signed URL - must succeed before proceeding
      const uploadResponse = await fetch(signedUrlInfo.url, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        // S3 upload failed - throw error to prevent form submission
        const errorText = await uploadResponse.text().catch(() => uploadResponse.statusText);
        throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}. ${errorText}`);
      }

      // Only set file data after successful S3 upload
      // Use file_info from API response to populate file_data payload
      if (!fileInfo) {
        throw new Error('File information not found in API response');
      }

      // Build file_data payload using file_info from API response
      const fileData = {
        url: fileInfo.url || '',
        original_file_name: fileInfo.original_file_name || file.name,
        s3_file_name: fileInfo.s3_file_name || '',
        bucket_name: fileInfo.bucket_name || '',
        file_type: fileInfo.file_type || 'document', // file_type from API (e.g., "document")
        file_size: file.size.toString(), // file_size from actual file
        path: fileInfo.path || signedUrlInfo.fields['key'] || '',
        content_type: fileInfo.file_type || signedUrlInfo.fields['Content-Type'] || file.type, // Use file_type from API, fallback to MIME type
      };

      setUploadedFileData(fileData);

      notification.show({
        title: 'Upload Successful',
        message: 'Document uploaded successfully. You can now save the requirement.',
        type: 'success',
        position: 'top-right',
        duration: 4000,
      });

    } catch (error) {
      console.error('File upload error:', error);

      // Clear file selection and document upload field on error
      setSelectedFile(null);
      handleCrdChange('documentUpload', '');
      setUploadedFileData(null);

      // Clear file input
      const fileInput = document.getElementById('fileInput') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }

      // Show error notification
      const errorMessage = 'Failed to upload file. Please try again.';

      notification.show({
        title: 'Upload Failed',
        message: errorMessage,
        type: 'error',
        position: 'top-right',
        duration: 5000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Check if file upload is in progress
    if (isUploading) {
      notification.show({
        title: 'Upload in Progress',
        message: 'Please wait for the file upload to complete before submitting.',
        type: 'warning',
        position: 'top-right',
        duration: 4000,
      });
      return;
    }

    // Check if document upload field is filled but upload hasn't completed successfully
    // When editing, allow existing file_data from the requirement
    const hasValidFileData = uploadedFileData || (requirement?.file_data && crd.documentUpload.trim());
    if (crd.documentUpload.trim() && !hasValidFileData) {
      notification.show({
        title: 'Upload Required',
        message: 'Please wait for the file upload to complete successfully before submitting. If upload failed, please try uploading again.',
        type: 'error',
        position: 'top-right',
        duration: 5000,
      });
      // Set error to highlight the field
      setErrors(prev => ({ ...prev, documentUpload: 'File upload must complete successfully before submission' }));
      return;
    }

    setLoading(true);
    try {
      // Convert work order status and CRD status to uppercase for API
      const workOrderForAPI = {
        ...workOrder,
        status: workOrder.status.toUpperCase() as any // "In Progress" -> "IN PROGRESS", "Initiated" -> "INITIATED"
      };

      const crdForAPI = {
        ...crd,
        status: crd.status.toUpperCase() as any // "Approved" -> "APPROVED", "Pending" -> "PENDING"
      };

      const requirementData: CreateRequirementRequest = {
        workOrder: workOrderForAPI,
        crd: crdForAPI,
      };

      let response;
      if (requirement) {
        // When editing, use uploadedFileData if a new file was uploaded, otherwise use existing file_data
        const fileDataToUse = uploadedFileData || requirement.file_data;
        response = await requirementService.updateRequirement(requirement.id.toString(), {
          id: requirement.id.toString(),
          workOrder: workOrderForAPI,
          crd: crdForAPI,
        }, fileDataToUse);
      } else {
        response = await requirementService.createRequirement(requirementData, uploadedFileData);
      }

      if (response.success && response.data) {
        // Show success toast
        notification.show({
          title: requirement ? 'Requirement Updated' : 'Requirement Created',
          message: response.message || `Requirement ${requirement ? 'updated' : 'created'} successfully`,
          type: 'success',
          position: 'top-right',
        });
        onRequirementAdded(response.data);
      } else {
        // Show error toast
        notification.show({
          title: requirement ? 'Update Failed' : 'Creation Failed',
          message: response.error || response.message || `Failed to ${requirement ? 'update' : 'create'} requirement`,
          type: 'error',
          position: 'top-right',
        });
      }
    } catch (error) {
      console.error(`Error ${requirement ? 'updating' : 'creating'} requirement:`, error);
      // Show error toast
      notification.show({
        title: 'Error',
        message: `An error occurred while ${requirement ? 'updating' : 'creating'} the requirement`,
        type: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  const resolveDocumentUrl = (): string | null => {
    if (uploadedFileData?.url) {
      return uploadedFileData.url;
    }
    if (uploadedFileData?.path) {
      return uploadedFileData.path;
    }
    if (requirement?.file_data?.url) {
      return requirement.file_data.url;
    }
    if (requirement?.file_data?.path) {
      return requirement.file_data.path;
    }
    return null;
  };

  const handleDocumentPreview = async (event?: React.MouseEvent<HTMLElement>) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!crd.documentUpload) {
      notification.show({
        title: 'No Document',
        message: 'Please upload a document before previewing.',
        type: 'warning',
        position: 'top-right',
      });
      return;
    }

    const documentUrl = resolveDocumentUrl();

    if (!documentUrl) {
      notification.show({
        title: 'Document Not Ready',
        message: 'The document reference is missing. Upload and save the file again.',
        type: 'error',
        position: 'top-right',
      });
      return;
    }

    try {
      const apiService = ApiService.getInstance();
      const response = await apiService.getFileSignedUrl(documentUrl, 3600);
      const signedUrl = response.data?.data?.s3_signed_url;

      if (response.success && signedUrl) {
        // Open signed URL in a new tab
        window.open(signedUrl, '_blank', 'noopener,noreferrer');
      } else {
        const errorMessage = response.data?.message || response.error || 'Unable to get document preview link';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error fetching signed URL for preview:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unable to load document preview.';
      notification.show({
        title: 'Preview Failed',
        message: errorMessage,
        type: 'error',
        position: 'top-right',
      });
    }
  };

  const handleWorkOrderChange = (field: keyof WorkOrder, value: string) => {
    setWorkOrder(prev => ({ ...prev, [field]: value }));
    // Clear error for the field (handle special case for status)
    const errorKey = field === 'status' ? 'woStatus' : field;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: undefined }));
    }
  };

  const handleCrdChange = (field: keyof ClientRequirementDocument, value: string | number | string[]) => {
    if (field === 'numberOfFiles') {
      const digitsOnly = String(value).replace(/\D/g, '');
      setCrd(prev => ({
        ...prev,
        numberOfFiles: digitsOnly === '' ? undefined : Number(digitsOnly),
      }));
      if (errors.numberOfFiles) {
        setErrors(prev => ({ ...prev, numberOfFiles: undefined }));
      }
      return;
    }

    setCrd(prev => ({ ...prev, [field]: value }));
    // Clear error for the field (handle special case for status)
    const errorKey = field === 'status' ? 'crdStatus' : field;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: undefined }));
    }
  };

  const primaryButtonLabel = requirement ? 'Update Requirement' : 'Save';


  return (
    <div className="requirement-form-wrapper" style={{ position: 'relative' }}>
      {loading && (
        <div
          className="requirement-form-loading-overlay"
          role="status"
          aria-live="polite"
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.55)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 5,
            backdropFilter: 'blur(1px)',
          }}
        >
          <img src={loadingGif} alt="Saving..." className="loading-gif" style={{ width: 48, height: 48, marginBottom: 12 }} />
          <p style={{ color: '#fff', fontSize: 14 }}>Saving requirement, please wait…</p>
        </div>
      )}
      <div className="requirement-form-header">
        <button
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'transparent',
            border: '1px solid #3a3f99',
            borderRadius: '6px',
            padding: '8px 16px',
            color: 'white',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseOver={e => {
            (e.currentTarget as HTMLElement).style.background = '#0a2a5a';
            (e.currentTarget as HTMLElement).style.borderColor = '#1454D4';
          }}
          onMouseOut={e => {
            (e.currentTarget as HTMLElement).style.background = 'transparent';
            (e.currentTarget as HTMLElement).style.borderColor = '#3a3f99';
          }}
        >
          <Icons.ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <h2>{requirement ? 'Edit Requirement' : 'Add New Requirement'}</h2>
      </div>

      <form
        onSubmit={handleSubmit}
        className="requirement-form-body"
      >
        <div className="requirement-form-grid">
            {/* Work Order Creation Section */}
            <div className="work-order-section">
              <h4>Work Order Creation</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="workOrderId">Work Order ID *</label>
                  <input
                    type="text"
                    id="workOrderId"
                    value={workOrder.workOrderId}
                    onChange={e => handleWorkOrderChange('workOrderId', e.target.value)}
                    className={errors.workOrderId ? 'error' : ''}
                    placeholder="WO20250915-001"
                    maxLength={50}
                  />
                  {errors.workOrderId && <div className="error-message">{errors.workOrderId}</div>}
                </div>

                <div className="form-group">
                  <label htmlFor="clientName">Client Name *</label>
                  <select
                    id="clientName"
                    value={workOrder.clientName}
                    onChange={e => {
                      const selectedClient = clients.find(c => (c.client_id || c.id)?.toString() === e.target.value);
                      if (selectedClient) {
                        handleWorkOrderChange('clientName', (selectedClient.client_id || selectedClient.id)?.toString() || '');
                      }
                    }}
                    className={errors.clientName ? 'error' : ''}
                  >
                    <option value="">Select client</option>
                    {clients.map(client => (
                      <option key={client.client_id || client.id} value={(client.client_id || client.id)?.toString()}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                  {errors.clientName && <div className="error-message">{errors.clientName}</div>}
                </div>

                <div className="form-group">
                  <label htmlFor="mouId">MoU ID *</label>
                  <input
                    type="text"
                    id="mouId"
                    value={workOrder.mouId}
                    onChange={e => handleWorkOrderChange('mouId', e.target.value)}
                    className={errors.mouId ? 'error' : ''}
                    placeholder="MOU-20250901"
                    maxLength={50}
                  />
                  {errors.mouId && <div className="error-message">{errors.mouId}</div>}
                </div>

                <div className="form-group">
                  <label htmlFor="studyId">Study ID *</label>
                  <input
                    type="text"
                    id="studyId"
                    value={workOrder.studyId}
                    onChange={e => handleWorkOrderChange('studyId', e.target.value)}
                    className={errors.studyId ? 'error' : ''}
                    placeholder="DR-STUDY-001"
                    maxLength={50}
                  />
                  {errors.studyId && <div className="error-message">{errors.studyId}</div>}
                </div>

                <div className="form-group full-width">
                  <label htmlFor="studyTitle">Study Title *</label>
                  <textarea
                  className={`textarea-inherit ${errors.studyTitle ? 'error' : ''}`}
                    id="studyTitle"
                    value={workOrder.studyTitle}
                    onChange={e => handleWorkOrderChange('studyTitle', e.target.value)}
                    rows={3}
                    maxLength={255}
                  />
                  {errors.studyTitle && <div className="error-message">{errors.studyTitle}</div>}
                </div>

                <div className="form-row three-cols">
                  <div className="form-group">
                    <label htmlFor="startDate">Start Date *</label>
                  <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                    <input
                      type="text"
                      value={workOrder.startDate ? moment(workOrder.startDate).format('DD MMM YYYY') : ''}
                      placeholder="dd MMM yyyy"
                      readOnly
                      onClick={() => {
                        const picker = document.getElementById('startDatePicker');
                        if (picker && 'showPicker' in picker) {
                          (picker as any).showPicker();
                        }
                      }}
                      style={{
                        background: '#090c29',
                        color: 'white',
                        border: errors.startDate ? '1px solid #ff4d4f' : '1px solid #3a3f99',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        paddingRight: '35px',
                        fontSize: '13px',
                        width: '100%',
                        cursor: 'pointer',
                      }}
                      className={errors.startDate ? 'error' : ''}
                    />
                    <span
                      onClick={() => {
                        const picker = document.getElementById('startDatePicker');
                        if (picker && 'showPicker' in picker) {
                          (picker as any).showPicker();
                        }
                      }}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                        color: '#9ca3af',
                        fontSize: '16px',
                      }}
                    >
                      📅
                    </span>
                    <input
                      id="startDatePicker"
                      type="date"
                      value={workOrder.startDate || ''}
                      max={workOrder.expectedCompletionDate || ''}
                      onChange={e => handleWorkOrderChange('startDate', e.target.value)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                        pointerEvents: 'none',
                      }}
                    />
                  </div>
                    {errors.startDate && <div className="error-message">{errors.startDate}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="expectedCompletionDate">Expected Completion Date *</label>
                    <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                      <input
                        type="text"
                        value={workOrder.expectedCompletionDate ? moment(workOrder.expectedCompletionDate).format('DD MMM YYYY') : ''}
                        placeholder="dd MMM yyyy"
                        readOnly
                        onClick={() => {
                          const picker = document.getElementById('expectedCompletionDatePicker');
                          if (picker && 'showPicker' in picker) {
                            (picker as any).showPicker();
                          }
                        }}
                        style={{
                          background: '#090c29',
                          color: 'white',
                          border: errors.expectedCompletionDate ? '1px solid #ff4d4f' : '1px solid #3a3f99',
                          borderRadius: '4px',
                          padding: '8px 12px',
                          paddingRight: '35px',
                          fontSize: '13px',
                          width: '100%',
                          cursor: 'pointer',
                        }}
                        className={errors.expectedCompletionDate ? 'error' : ''}
                      />
                      <span
                        onClick={() => {
                          const picker = document.getElementById('expectedCompletionDatePicker');
                          if (picker && 'showPicker' in picker) {
                            (picker as any).showPicker();
                          }
                        }}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          cursor: 'pointer',
                          pointerEvents: 'auto',
                          color: '#9ca3af',
                          fontSize: '16px',
                        }}
                      >
                        📅
                      </span>
                      <input
                        id="expectedCompletionDatePicker"
                        type="date"
                        value={workOrder.expectedCompletionDate || ''}
                        min={workOrder.startDate || ''}
                        onChange={e => handleWorkOrderChange('expectedCompletionDate', e.target.value)}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          opacity: 0,
                          cursor: 'pointer',
                          pointerEvents: 'none',
                        }}
                      />
                    </div>
                    {errors.expectedCompletionDate && <div className="error-message">{errors.expectedCompletionDate}</div>}
                    </div>

                    <div className="form-group">
                    <label htmlFor="status">Status *</label>
                    <select
                      id="status"
                      value={workOrder.status || ''}
                      onChange={e => handleWorkOrderChange('status', e.target.value as any)}
                      className={errors.woStatus ? 'error' : ''}
                    >
                      <option value="">Select status</option>
                      <option value="Initiated">Initiated</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                    {errors.woStatus && <div className="error-message">{errors.woStatus}</div>}
                  </div>
                </div>
              </div>
            </div>

            {/* Client Requirement Document Section */}
            <div className="crd-section">
              <h4>Client Requirement Document (CRD)</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="crdId">CRD ID *</label>
                  <input
                    type="text"
                    id="crdId"
                    value={crd.crdId}
                    onChange={e => handleCrdChange('crdId', e.target.value)}
                    className={errors.crdId ? 'error' : ''}
                    placeholder="CRD-20250920-001"
                    maxLength={50}
                  />
                  {errors.crdId && <div className="error-message">{errors.crdId}</div>}
                </div>

                <div className="form-group">
                  <label htmlFor="requirementTitle">Requirement Title *</label>
                  <input
                    type="text"
                    id="requirementTitle"
                    value={crd.requirementTitle}
                    onChange={e => handleCrdChange('requirementTitle', e.target.value)}
                    className={errors.requirementTitle ? 'error' : ''}
                    placeholder="OCT Grading for DR Patients"
                    maxLength={255}
                  />
                  {errors.requirementTitle && <div className="error-message">{errors.requirementTitle}</div>}
                </div>

                <div className="form-group">
                  <label htmlFor="imagingModalities">Imaging Modalities *</label>
                  <ModalityMultiSelect
                    value={crd.imagingModalities}
                    onChange={selected => handleCrdChange('imagingModalities', selected)}
                  />
                  {errors.imagingModalities && <div className="error-message">{errors.imagingModalities}</div>}
                </div>

                <div className="form-row two-cols">
                  <div className="form-group">
                    <label htmlFor="fileFormat">File Format *</label>
                    <FileFormatMultiSelect
                      value={crd.fileFormat}
                      onChange={selected => handleCrdChange('fileFormat', selected)}
                    />
                    {errors.fileFormat && <div className="error-message">{errors.fileFormat}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="numberOfFiles">Number of Files *</label>
                    <input
                      type="text"
                      id="numberOfFiles"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      onWheel={event => {
                        event.preventDefault();
                        event.currentTarget.blur();
                      }}
                      value={crd.numberOfFiles ?? ''}
                      onChange={e => handleCrdChange('numberOfFiles', e.target.value)}
                      className={errors.numberOfFiles ? 'error' : ''}
                      placeholder=""
                    />
                    {errors.numberOfFiles && <div className="error-message">{errors.numberOfFiles}</div>}
                  </div>
                </div>

                <div className="form-group full-width">
                  <label htmlFor="annotationRequirements">Annotation Requirements *</label>
                  <textarea
                    className={`textarea-inherit ${errors.annotationRequirements ? 'error' : ''}`}
                    id="annotationRequirements"
                    value={crd.annotationRequirements}
                    onChange={e => handleCrdChange('annotationRequirements', e.target.value)}
                    rows={3}
                    maxLength={1000}
                  />
                  {errors.annotationRequirements && <div className="error-message">{errors.annotationRequirements}</div>}
                </div>

                <div className="form-row three-cols">
                  <div className="form-group">
                    <label htmlFor="reportingFormat">Reporting Format *</label>
                    <select
                      id="reportingFormat"
                      value={crd.reportingFormat}
                      onChange={e => handleCrdChange('reportingFormat', e.target.value)}
                      className={errors.reportingFormat ? 'error' : ''}
                    >
                      <option value="">Select</option>
                      {REPORTING_FORMAT_OPTIONS.map(option => (
                        <option key={option} value={option} disabled={option === 'PDF'}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {errors.reportingFormat && <div className="error-message">{errors.reportingFormat}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="signedOffBy">Signed Off By *</label>
                    <input
                      type="text"
                      id="signedOffBy"
                      value={crd.signedOffBy}
                      onChange={e => handleCrdChange('signedOffBy', e.target.value)}
                      className={errors.signedOffBy ? 'error' : ''}
                      placeholder="Dr. A. Kumar"
                      maxLength={255}
                    />
                    {errors.signedOffBy && <div className="error-message">{errors.signedOffBy}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="signOffDate">Sign-off Date *</label>
                  <div className="date-input-wrapper">
                    <input
                      type="text"
                      value={crd.signOffDate ? moment(crd.signOffDate).format('DD MMM YYYY') : ''}
                      placeholder="dd MMM yyyy"
                      readOnly
                      onClick={() => {
                        const picker = document.getElementById('signOffDatePicker');
                        if (picker && 'showPicker' in picker) {
                          (picker as any).showPicker();
                        }
                      }}
                      className={`date-input-display ${errors.signOffDate ? 'error' : ''}`}
                    />
                    <span
                      onClick={() => {
                        const picker = document.getElementById('signOffDatePicker');
                        if (picker && 'showPicker' in picker) {
                          (picker as any).showPicker();
                        }
                      }}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                        color: '#9ca3af',
                        fontSize: '16px',
                      }}
                    >
                      📅
                    </span>
                    <input
                      id="signOffDatePicker"
                      type="date"
                      value={crd.signOffDate || ''}
                      onChange={e => handleCrdChange('signOffDate', e.target.value)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                        pointerEvents: 'none',
                      }}
                    />
                  </div>
                    {errors.signOffDate && <div className="error-message">{errors.signOffDate}</div>}
                  </div>
                </div>

                <div className="form-row status-doc-row">
                  <div className="form-group">
                    <label htmlFor="crdStatus">Status *</label>
                    <select
                      id="crdStatus"
                      value={crd.status || ''}
                      onChange={e => handleCrdChange('status', e.target.value as any)}
                      className={errors.crdStatus ? 'error' : ''}
                    >
                      <option value="">Select status</option>
                      <option value="Approved">Approved</option>
                      <option value="Pending">Pending</option>
                      <option value="Rejected">Rejected</option>
                    </select>
                    {errors.crdStatus && <div className="error-message">{errors.crdStatus}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="documentUpload">Document Upload *</label>
                    <div className="file-upload-group">
                      <input
                        type="text"
                        id="documentUpload"
                        value={crd.documentUpload}
                        onChange={e => handleCrdChange('documentUpload', e.target.value)}
                        className={errors.documentUpload ? 'error' : ''}
                        placeholder="Document.pdf"
                        readOnly
                        onClick={crd.documentUpload ? handleDocumentPreview : undefined}
                        title={crd.documentUpload ? 'Click to preview document' : undefined}
                        style={{
                          cursor: crd.documentUpload ? 'pointer' : 'default',
                        }}
                      />
                      <input
                        type="file"
                        id="fileInput"
                        style={{ display: 'none' }}
                        accept=".pdf,.doc,.docx"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setSelectedFile(file);
                            handleCrdChange('documentUpload', file.name);
                            await handleFileUpload(file);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="upload-button"
                        onClick={() => document.getElementById('fileInput')?.click()}
                        disabled={isUploading}
                      >
                        {isUploading ? 'Uploading...' : '↑ Upload File'}
                      </button>
                    </div>
                    <small className="upload-hint">
                      Scanned document or soft copy. Click the file name to preview.
                    </small>
                    {errors.documentUpload && <div className="error-message">{errors.documentUpload}</div>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>

        <div className="requirement-form-footer">
          <button
            type="button"
            className="admin-button secondary"
            onClick={onBack}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="admin-button primary"
            onClick={handleSubmit}
            disabled={loading}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
          >
            {loading && (
              <img
                src={loadingGif}
                alt="Saving..."
                style={{ width: 18, height: 18 }}
              />
            )}
            {primaryButtonLabel}
          </button>
        </div>
    </div>
  );
};

export default Admin;

// Protocol Editor (inline, placeholder implementation)
interface ProtocolEditorProps {
  protocol: Protocol | null;
  isDuplicating?: boolean;
  onBack: () => void;
  onSave: () => void;
  crdDocs: { id: number; crd_id: string }[];
  managers: { id: number; username: string }[];
}

type ParamError = {
  name?: string;
  numericUnit?: string;
  numericMin?: string;
  numericMax?: string;
  percentageDisparity?: string;
  dropdownOptions?: string;
  checkboxOptions?: string;
  typeRequired?: string;
};

/** Digits 0–9 only (type="text" + inputMode for Firefox-friendly numeric entry). */
function parseDigitsOnlyField(raw: string): number | '' {
  const digits = raw.replace(/\D/g, '');
  return digits === '' ? '' : Number(digits);
}

function formatDigitsOnlyField(value: number | ''): string {
  return value === '' ? '' : String(Math.trunc(value));
}

const ProtocolEditor: React.FC<ProtocolEditorProps> = ({
  protocol,
  isDuplicating = false,
  onBack,
  onSave,
  crdDocs,
  managers,
}) => {
  const protocolService = ProtocolService.getInstance();
  const notification = useNotification();
  const isDuplicateMode = !!isDuplicating;
  const isPublished = protocol?.status?.toLowerCase() === 'published';
  const isAborted = protocol?.status?.toLowerCase() === 'abort' || protocol?.status?.toLowerCase() === 'aborted';
  const computeInitialViewOnly = () => !isDuplicateMode && (isPublished || isAborted); // Published/aborted are read-only unless duplicating or upgrading
  const [isViewOnly, setIsViewOnly] = useState<boolean>(computeInitialViewOnly());
  const [showUpgradeConfirmation, setShowUpgradeConfirmation] = useState(false);
  const [availableCrdIds, setAvailableCrdIds] = useState<string[]>([]);
  const [crdId, setCrdId] = useState('');
  const [protocolName, setProtocolName] = useState(protocol?.name || '');
  const [description, setDescription] = useState(protocol?.description || '');
  const [isActive, setIsActive] = useState<boolean>(protocol ? !!protocol.is_active : true);
  const [effectiveFrom, setEffectiveFrom] = useState<string>(() => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  });
  const [graders, setGraders] = useState<number[]>([]);
  const [availableGradersLocal, setAvailableGradersLocal] = useState<{ id: number; username: string }[]>([]);
  const [availableManagersLocal, setAvailableManagersLocal] = useState<{ id: number; username: string }[]>(managers || []);
  const [escalateToPerson, setEscalateToPerson] = useState<number | null>(null);
  const [averageTimeForGrading, setAverageTimeForGrading] = useState<number | ''>('');
  const [fastThreshold, setFastThreshold] = useState<number | ''>('');
  const [slowThreshold, setSlowThreshold] = useState<number | ''>('');
  const [reviewType, setReviewType] = useState<'2 eye' | '4 eye'>('2 eye');
  const [randomSamplingPercentage, setRandomSamplingPercentage] = useState<number | ''>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [isPrintingProtocol, setIsPrintingProtocol] = useState(false);
  const [paramErrors, setParamErrors] = useState<Record<string, ParamError>>({});
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationMessages, setValidationMessages] = useState<string[]>([]);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [pendingSaveStatus, setPendingSaveStatus] = useState<'Published' | 'Draft' | null>(null);
  const [upgradeReason, setUpgradeReason] = useState<string>('');

  // Disparity threshold is only applicable for 4 eye review.
  // Only clear thresholds when switching from 4 eye -> 2 eye (avoid clearing during initial load).
  const prevReviewTypeRef = useRef(reviewType);
  useEffect(() => {
    const prev = prevReviewTypeRef.current;
    if (prev === '4 eye' && reviewType === '2 eye') {
      setParams(prevParams =>
        prevParams.map(p => ({
          ...p,
          percentageDisparity: undefined,
        }))
      );
      // Also clear any disparity-specific validation errors when leaving 4 eye.
      setParamErrors(prevErrs => {
        const next: typeof prevErrs = {};
        Object.entries(prevErrs).forEach(([id, pe]) => {
          if (!pe?.percentageDisparity) {
            next[id] = pe;
            return;
          }
          const { percentageDisparity: _omit, ...rest } = pe;
          next[id] = rest;
        });
        return next;
      });
    }
    prevReviewTypeRef.current = reviewType;
  }, [reviewType]);

  // Reset view-only state whenever a different protocol is opened
  useEffect(() => {
    const nextViewOnly =
      !isDuplicateMode &&
      (protocol?.status?.toLowerCase() === 'published' ||
        protocol?.status?.toLowerCase() === 'abort' ||
        protocol?.status?.toLowerCase() === 'aborted');
    setShowUpgradeConfirmation(false);
    setUpgradeReason('');
    setIsViewOnly(nextViewOnly);
  }, [protocol, isDuplicateMode]);

  // Initialize editor when editing an existing protocol
  useEffect(() => {
    if (protocol) {
      try {
        // Initialize graders
        if (Array.isArray(protocol.assignees)) {
          setGraders(protocol.assignees.map((a: any) => Number(a.id)).filter(Boolean));
        }
        // Initialize escalate to person (support multiple API field shapes)
        const rawEscalate =
          (protocol as any).escalate_to_person_id ??
          (protocol as any).escalate_to_id ??
          (protocol as any).escalate_to_person ??
          (protocol as any).escalate_to;
        if (rawEscalate !== undefined && rawEscalate !== null) {
          const escalateId =
            typeof rawEscalate === 'object' && rawEscalate.id !== undefined
              ? rawEscalate.id
              : rawEscalate;
          setEscalateToPerson(Number(escalateId));
        } else {
          setEscalateToPerson(null);
        }
        // Initialize average time for grading - keep blank, don't autopopulate
        // Only set if it's a valid non-zero value
        if (protocol.expected_time !== undefined && protocol.expected_time !== null && protocol.expected_time > 0) {
          setAverageTimeForGrading(protocol.expected_time);
        } else {
          setAverageTimeForGrading('');
        }
        const rawFastThreshold = (protocol as any).fast_threshold;
        if (
          rawFastThreshold !== undefined &&
          rawFastThreshold !== null &&
          !Number.isNaN(Number(rawFastThreshold)) &&
          Number(rawFastThreshold) > 0
        ) {
          setFastThreshold(Number(rawFastThreshold));
        } else {
          setFastThreshold('');
        }
        const rawSlowThreshold = (protocol as any).slow_threshold;
        if (
          rawSlowThreshold !== undefined &&
          rawSlowThreshold !== null &&
          !Number.isNaN(Number(rawSlowThreshold)) &&
          Number(rawSlowThreshold) > 0
        ) {
          setSlowThreshold(Number(rawSlowThreshold));
        } else {
          setSlowThreshold('');
        }
        // Initialize grading type (review type) and sampling percentage from protocol
        const rawGradingType =
          (protocol as any).grading_type ??
          (protocol as any).review_type ??
          (protocol as any).reviewType;
        if (rawGradingType === 'FOUR_EYES' || rawGradingType === '4 eye') {
          setReviewType('4 eye');
        } else {
          // Default and for 'TWO_EYES'
          setReviewType('2 eye');
        }
        const rawSampling =
          (protocol as any).sampling_percentage ??
          (protocol as any).random_sampling_percentage ??
          (protocol as any).randomSamplingPercentage;
        if (rawSampling !== undefined && rawSampling !== null && !Number.isNaN(Number(rawSampling))) {
          const val = Number(rawSampling);
          setRandomSamplingPercentage(val >= 0 && val <= 100 ? val : '');
        } else {
          setRandomSamplingPercentage('');
        }
        // Initialize CRD from requirement_doc_id - map numeric ID to CRD string
        if ((protocol as any).requirement_doc_id) {
          const reqDocId = (protocol as any).requirement_doc_id;
          const matchedDoc = crdDocs.find(doc => doc.id === reqDocId);
          if (matchedDoc) {
            setCrdId(matchedDoc.crd_id);
          } else {
            // Fallback: if no mapping found, try to use it as string
            setCrdId(String(reqDocId));
          }
        }
        // Initialize parameters from protocol.parameters
        if (Array.isArray(protocol.parameters) && protocol.parameters.length) {
          const toRows = (nodes: any[], parentId?: string): any[] =>
            nodes.flatMap((n: any) => {
              const rowId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
              const fields = Array.isArray(n.parameter_fields) ? n.parameter_fields : [];
              // Extract field details
              const numericField = fields.find((f: any) => {
                const elementType = (f.element_type || '').toLowerCase();
                // Check for new format: element_type === 'Numeric'
                // Also support backward compatibility with old format: element_type === 'textbox' with numeric label/type
                return elementType === 'numeric' ||
                  (elementType === 'textbox' && (((f.type || '').toLowerCase() === 'numeric') || (String(f.label || '').toLowerCase().includes('numeric'))));
              });
              const dropdownField = fields.find((f: any) => (f.element_type || '').toLowerCase() === 'dropdown');
              const checkboxField = fields.find((f: any) => (f.element_type || '').toLowerCase() === 'checkbox');
              const freeTextField = fields.find(
                (f: any) =>
                  (f.element_type || '').toLowerCase() === 'textbox' &&
                  (
                    String(f.label || '').toLowerCase().includes('freetext') ||
                    (f.type || '').toLowerCase() === 'freetext'
                  )
              );
              const remarksField = fields.find((f: any) => (f.element_type || '').toLowerCase() === 'textbox' && String(f.label || '').toLowerCase().includes('remark'));

              const dropdownOptions = Array.isArray(dropdownField?.values) ? dropdownField.values.map((v: any) => String(v)) : [];
              const checkboxOptions = Array.isArray(checkboxField?.values) ? checkboxField.values.map((v: any) => String(v)) : [];

              // Children can come in different shapes depending on API
              const rawChildren = Array.isArray(n.children) && n.children.length
                ? n.children
                : (Array.isArray((n as any).parameters) && (n as any).parameters.length ? (n as any).parameters : []);

              // Store original field IDs for updating
              const originalFieldIds: ParamRow['originalFieldIds'] = {};
              if (numericField?.id) originalFieldIds.numeric = numericField.id;
              if (dropdownField?.id) originalFieldIds.dropdown = dropdownField.id;
              if (checkboxField?.id) originalFieldIds.checkbox = checkboxField.id;
              if (freeTextField?.id) originalFieldIds.freeText = freeTextField.id;
              if (remarksField?.id) originalFieldIds.remark = remarksField.id;

              // Load disparity threshold (may be stored under newer or legacy keys)
              const rawDisp =
                (numericField as any)?.disparity_threshold ??
                (numericField as any)?.percentage_disparity ??
                (numericField as any)?.percentageDisparity;
              const percentageDisparity =
                rawDisp !== undefined && rawDisp !== null && !Number.isNaN(Number(rawDisp))
                  ? Number(rawDisp)
                  : undefined;

              const newRow: ParamRow = {
                id: rowId,
                name: String(n.parameter_name || ''),
                isRoot: (n.is_root && rawChildren.length > 0) ? true : false, // Only check isRoot if parameter has children AND is_root is true
                parentId,
                numeric: {
                  enabled: !!numericField,
                  unit: numericField ? String(numericField.type || numericField.label || 'Numeric') : undefined,
                  min:
                    (numericField as any)?.range?.min !== undefined
                      ? Number((numericField as any).range.min)
                      : (numericField as any)?.min !== undefined
                        ? Number((numericField as any).min)
                        : undefined,
                  max:
                    (numericField as any)?.range?.max !== undefined
                      ? Number((numericField as any).range.max)
                      : (numericField as any)?.max !== undefined
                        ? Number((numericField as any).max)
                        : undefined,
                },
                dropdown: { enabled: !!dropdownField, options: dropdownOptions },
                checkbox: { enabled: !!checkboxField, options: checkboxOptions },
                // Mark freetext true if there's a freetext field (can coexist with other fields)
                freeText: !!freeTextField,
                remark: remarksField ? 'enabled' : '',
                percentageDisparity,
                // Store original IDs for updating
                originalParameterId: n.id,
                originalFieldIds: Object.keys(originalFieldIds).length > 0 ? originalFieldIds : undefined,
              };
              const children = rawChildren.length ? toRows(rawChildren, rowId) : [];
              return [newRow, ...children];
            });
          const flat = toRows(protocol.parameters, undefined);
          if (flat.length) setParams(flat);
        }
      } catch (e) {
        console.warn('Failed to initialize editor from protocol:', e);
      }
    }
  }, [protocol, crdDocs]);

  // Pull preloaded lists from parent if available via window event; otherwise fetch once here as fallback
  useEffect(() => {
    const handler = (e: any) => {
      if (Array.isArray(e.detail?.crdIds)) {
        setAvailableCrdIds(e.detail.crdIds);
      }
      if (Array.isArray(e.detail?.graders)) {
        setAvailableGradersLocal(e.detail.graders);
      }
    };
    window.addEventListener('crd-list-preloaded', handler as any);
    window.addEventListener('graders-preloaded', handler as any);

    // Fallback fetch if nothing preloaded
    (async () => {
      if (availableCrdIds.length === 0) {
        try {
          const resp = await protocolService.getRequirementDocs();
          if (resp.is_success && resp.data?.[0]?.requirement_docs) {
            // Filter only APPROVED CRDs (case-insensitive)
            const approvedDocs = resp.data[0].requirement_docs.filter((d: any) => d && d.status && d.status.toLowerCase() === 'approved');
            setAvailableCrdIds(approvedDocs.map((d: any) => String(d.crd_id)));
          }
        } catch (err) {
          console.warn('Fallback CRD fetch failed:', err);
        }
      }
      if (availableGradersLocal.length === 0) {
        try {
          const api = ApiService.getInstance();
          const gradersResp = await api.get<any>('/grader/get-users', undefined, api.getAuthServiceUrl());
          if (gradersResp.success) {
            const rawData = Array.isArray(gradersResp.data?.data)
              ? gradersResp.data.data
              : Array.isArray(gradersResp.data)
                ? gradersResp.data
                : [];
            let usersList: any[] = [];

            if (Array.isArray(rawData)) {
              rawData.forEach(item => {
                if (Array.isArray(item?.users)) {
                  usersList = usersList.concat(item.users);
                } else if (item) {
                  usersList.push(item);
                }
              });
            } else if (Array.isArray(rawData?.users)) {
              usersList = rawData.users;
            }

            const mapped = usersList
              .map((u: any) => {
                const formattedName = formatNameByCountry(
                  u.first_name || '',
                  u.last_name || '',
                  u.middle_name || '',
                  'IND'
                );
                const displayName = formattedName !== '-' ? formattedName : (u.username || '-');

                return {
                  id: Number(u.id ?? u.user_id ?? u.ID ?? 0),
                  username: String(displayName).trim(),
                };
              })
              .filter((u: any) => u.id && u.username && u.username !== '-');

            setAvailableGradersLocal(mapped);
          }
        } catch (err) {
          console.warn('Fallback graders fetch failed:', err);
        }
      }
    })();

    return () => {
      window.removeEventListener('crd-list-preloaded', handler as any);
      window.removeEventListener('graders-preloaded', handler as any);
    };
  }, []);

  // Update managers list when prop changes
  useEffect(() => {
    if (managers && managers.length > 0) {
      setAvailableManagersLocal(managers);
    }
  }, [managers]);

  // Ensure escalate-to id is visible in dropdown options even if not returned in the managers list
  useEffect(() => {
    if (!escalateToPerson) return;
    const exists = availableManagersLocal.some(m => Number(m.id) === Number(escalateToPerson));
    if (!exists) {
      const fallbackName =
        (protocol as any)?.escalate_to_person_name ||
        (protocol as any)?.escalate_to_person?.username ||
        `User #${escalateToPerson}`;
      setAvailableManagersLocal(prev => [...prev, { id: Number(escalateToPerson), username: String(fallbackName) }]);
    }
  }, [escalateToPerson, availableManagersLocal, protocol]);

  // Parameter tree state
  type ParamRow = {
    id: string;
    name: string;
    isRoot: boolean;
    parentId?: string;
    numeric: { enabled: boolean; unit?: string; min?: number; max?: number };
    dropdown: { enabled: boolean; options: string[]; input?: string };
    checkbox: { enabled: boolean; options: string[]; input?: string };
    freeText: boolean;
    remark: string;
    /** Disparity threshold (%) - numeric, for numeric parameters only */
    percentageDisparity?: number;
    // Store original parameter ID from protocol data
    originalParameterId?: number;
    // Store original field IDs mapped by field type
    originalFieldIds?: {
      numeric?: number;
      dropdown?: number;
      checkbox?: number;
      freeText?: number;
      remark?: number;
    };
  };
  const [params, setParams] = useState<ParamRow[]>([]);

  const addParam = (parentId?: string) => {
    const id = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
    const newRow: ParamRow = {
      id,
      name: '',
      isRoot: false, // Default to unchecked (regular parameter)
      parentId,
      numeric: { enabled: false, unit: undefined, min: undefined, max: undefined },
      dropdown: { enabled: false, options: [] },
      checkbox: { enabled: false, options: [] },
      freeText: false,
      remark: '',
      percentageDisparity: undefined,
    };
    setParams(prev => {
      // If no parent provided, append to the end
      if (!parentId) {
        return [...prev, newRow];
      }

      // Find the parent index
      const parentIndex = prev.findIndex(p => p.id === parentId);
      if (parentIndex === -1) {
        // Fallback: if parent not found, append to the end
        return [...prev, newRow];
      }

      // Insert after the parent and after any existing siblings
      let insertIndex = parentIndex + 1;
      while (insertIndex < prev.length && prev[insertIndex].parentId === parentId) {
        insertIndex += 1;
      }

      const next = [...prev.slice(0, insertIndex), newRow, ...prev.slice(insertIndex)];
      return next;
    });
  };

  const updateParam = (id: string, updater: (p: ParamRow) => ParamRow) => {
    setParams(prev => prev.map(p => (p.id === id ? updater({ ...p }) : p)));
  };

  const addOption = (id: string, kind: 'dropdown' | 'checkbox') => {
    updateParam(id, p => {
      const inputKey = 'input' as const;
      const inputVal = p[kind][inputKey]?.trim();
      if (inputVal) {
        p[kind].options = Array.from(new Set([...(p[kind].options || []), inputVal]));
        p[kind][inputKey] = '';
      }
      return p;
    });
  };

  const childCount = (parentId: string) => params.filter(pr => pr.parentId === parentId).length;

  const validateForm = (): { ok: boolean; errors: Record<string, string>; paramErrors: Record<string, ParamError> } => {
    const newErrors: Record<string, string> = {};
    const newParamErrors: Record<string, ParamError> = {};

    // Protocol name validation
    if (!protocolName.trim()) {
      newErrors.protocolName = 'Protocol name is required';
    } else if (protocolName.length < 3) {
      newErrors.protocolName = 'Protocol name must be at least 3 characters';
    }

    // Description validation: only require a non-empty value
    if (!description.trim()) {
      newErrors.description = 'Description is required';
    }

    // CRD ID validation
    if (!crdId.trim()) {
      newErrors.crdId = 'CRD ID is required';
    }

    // Effective date validation
    if (!effectiveFrom) {
      newErrors.effectiveFrom = 'Effective date is required';
    } else {
      const selectedDate = new Date(effectiveFrom);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.effectiveFrom = 'Effective date cannot be in the past';
      }
    }

    // Average time for grading validation
    if (averageTimeForGrading === '' || averageTimeForGrading === null || averageTimeForGrading === undefined) {
      newErrors.averageTimeForGrading = 'Average time for grading is required';
    } else if (Number(averageTimeForGrading) <= 0) {
      newErrors.averageTimeForGrading = 'Average time for grading must be greater than 0';
    }

    // QC time thresholds (minutes)
    if (fastThreshold === '' || fastThreshold === null || fastThreshold === undefined) {
      newErrors.fastThreshold = 'Fast threshold is required';
    } else if (Number(fastThreshold) <= 0) {
      newErrors.fastThreshold = 'Fast threshold must be greater than 0';
    }
    if (slowThreshold === '' || slowThreshold === null || slowThreshold === undefined) {
      newErrors.slowThreshold = 'Slow threshold is required';
    } else if (Number(slowThreshold) <= 0) {
      newErrors.slowThreshold = 'Slow threshold must be greater than 0';
    }
    if (
      fastThreshold !== '' &&
      slowThreshold !== '' &&
      Number(fastThreshold) > 0 &&
      Number(slowThreshold) > 0 &&
      Number(fastThreshold) >= Number(slowThreshold)
    ) {
      newErrors.fastThreshold = 'Fast threshold must be less than slow threshold';
      newErrors.slowThreshold = 'Slow threshold must be greater than fast threshold';
    }

    // Graders validation
    if (graders.length === 0) {
      newErrors.graders = 'At least one grader must be selected';
    }
    // If review type is 4 eye, at least 2 graders must be selected
    if (reviewType === '4 eye' && graders.length < 2) {
      newErrors.graders = 'For 4 eye review, at least 2 graders must be selected';
    }

    // Random sampling percentage: 0–100
    if (randomSamplingPercentage === '' || randomSamplingPercentage === null || randomSamplingPercentage === undefined) {
      newErrors.randomSamplingPercentage = 'Random sampling percentage is required';
    } else {
      const pct = Number(randomSamplingPercentage);
      if (pct < 0 || pct > 100) {
        newErrors.randomSamplingPercentage = 'Random sampling percentage must be between 0 and 100';
      }
    }

    // Escalate To validation
    if (escalateToPerson === null || escalateToPerson === undefined) {
      newErrors.escalateToPerson = 'Escalate To is required';
    }

    // Parameters validation
    if (params.length === 0) {
      newErrors.parameters = 'At least one parameter is required';
    }

    // Root parameter rule: each root must have at least one child
    const rootParams = params.filter(p => p.isRoot);
    for (const root of rootParams) {
      const hasChild = params.some(p => p.parentId === root.id);
      if (!hasChild) {
        newErrors.parameters = 'Each root parameter must include at least one child parameter';
        break;
      }
    }

    // Per-parameter detailed validations
    params.forEach(p => {
      const errs: ParamError = {};
      if (!p.name.trim()) {
        errs.name = 'Parameter name is required';
      }
      // Ensure at least one value type is selected (not required for root rows)
      if (!p.isRoot || p.parentId) {
        const hasAnyType = !!(
          p.numeric.enabled ||
          p.dropdown.enabled ||
          p.checkbox.enabled ||
          p.freeText ||
          p.remark === 'enabled'
        );
        if (!hasAnyType) {
          errs.typeRequired = 'Select at least one value type (Numeric, Dropdown, Checkbox, Free Text, or Remark)';
        }
      }
      if (p.numeric.enabled && !(p.numeric.unit && String(p.numeric.unit).trim())) {
        errs.numericUnit = 'Unit is required for numeric parameter';
      }
      if (p.numeric.enabled && (p.numeric.min === undefined || p.numeric.min === null)) {
        errs.numericMin = 'Min value is required for numeric parameter';
      }
      if (p.numeric.enabled && (p.numeric.max === undefined || p.numeric.max === null)) {
        errs.numericMax = 'Max value is required for numeric parameter';
      }
      // Disparity threshold is only applicable (and required) for 4 eye review + numeric parameters
      if (reviewType === '4 eye' && p.numeric.enabled) {
        if (p.percentageDisparity === undefined || p.percentageDisparity === null || Number.isNaN(Number(p.percentageDisparity))) {
          errs.percentageDisparity = 'Disparity threshold is required for 4 eye review';
        } else {
          const disp = Number(p.percentageDisparity);
          if (disp < 0 || disp > 100) {
            errs.percentageDisparity = 'Disparity threshold must be between 0 and 100';
          }
        }
      }
      if (p.dropdown.enabled && (p.dropdown.options?.length || 0) < 2) {
        errs.dropdownOptions = 'Provide at least 2 options';
      }
      if (p.checkbox.enabled && (p.checkbox.options?.length || 0) < 1) {
        errs.checkboxOptions = 'Provide at least 1 option';
      }
      if (Object.keys(errs).length) {
        newParamErrors[p.id] = errs;
      }
    });

    setErrors(newErrors);
    setParamErrors(newParamErrors);
    return { ok: Object.keys(newErrors).length === 0 && Object.keys(newParamErrors).length === 0, errors: newErrors, paramErrors: newParamErrors };
  };

  const handleSave = async (statusToSave: 'Published' | 'Draft' = 'Published') => {
    const result = validateForm();
    if (!result.ok) {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (_) {}
      const msgs: string[] = [];
      Object.entries(result.errors).forEach(([key, val]) => {
        msgs.push(val);
      });
      // Map param errors to human-friendly lines
      params.forEach((p, idx) => {
        const pe = result.paramErrors[p.id] as ParamError | undefined;
        if (!pe) return;

        // Build concise, more natural fragments for the modal message only
        const fragments: string[] = [];
        if (pe.name) fragments.push('parameter name');
        if (pe.numericUnit) fragments.push('numeric unit');
        if (pe.numericMin) fragments.push('numeric min value');
        if (pe.numericMax) fragments.push('numeric max value');
        if (pe.percentageDisparity) fragments.push('disparity threshold (%)');
        if (pe.dropdownOptions) fragments.push('at least 2 dropdown options');
        if (pe.checkboxOptions) fragments.push('at least 1 checkbox option');

        if (fragments.length) {
          msgs.push(`Parameter row ${idx + 1}: Please provide ${fragments.join(', ')}.`);
        }
        if (pe.typeRequired) {
          msgs.push(`Parameter row ${idx + 1}: Select at least one value type (Numeric, Dropdown, Checkbox, Free Text, or Remark).`);
        }
      });
      setValidationMessages(msgs);
      setShowValidationModal(true);
      return;
    }

    // For Draft, save immediately without confirmation
    // For Published, show confirmation dialog
    if (statusToSave === 'Draft') {
      await performSave(statusToSave);
    } else {
      setPendingSaveStatus(statusToSave);
      setShowSaveConfirmation(true);
    }
  };

  const performSave = async (statusToSave: 'Published' | 'Draft') => {
    setLoading(true);
    try {
      const payload = buildProtocolPayload(statusToSave);

      // On edit, send protocol_id in the same create API payload.
      // For duplicate, we intentionally DO NOT send protocol_id so a new protocol is created.
      if (protocol?.id && !isDuplicateMode) {
        payload.protocol_id = protocol.id;
      }

      const response = await protocolService.createProtocol(payload as any);
      if (response.is_success) {
        const isEditMode = !!protocol && !isDuplicateMode;
        // Show success toast
        notification.show({
          title: isEditMode ? 'Successfully updated' : 'Successfully created',
          message: isEditMode ? 'Protocol has been updated' : 'Protocol has been created',
          type: 'success',
          duration: 3000,
          position: 'top-right',
        });
        // Refresh protocol list via callback
        onSave();
        onBack();
      } else {
        // Show error toast
        const isEditMode = !!protocol && !isDuplicateMode;
        notification.show({
          title: isEditMode ? 'Failed to update protocol' : 'Failed to create protocol',
          message: response.message || 'Please try again',
          type: 'error',
          duration: 5000,
          position: 'top-right',
        });
      }
    } catch (error) {
      console.error('Error saving protocol:', error);
      // Show error toast
      const isEditMode = !!protocol && !isDuplicateMode;
      notification.show({
        title: isEditMode ? 'Failed to update protocol' : 'Failed to create protocol',
        message: 'An error occurred. Please try again',
        type: 'error',
        duration: 5000,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSave = async () => {
    if (pendingSaveStatus) {
      setShowSaveConfirmation(false);
      await performSave(pendingSaveStatus);
      setPendingSaveStatus(null);
    }
  };

  const handleCancelSave = () => {
    setShowSaveConfirmation(false);
    setPendingSaveStatus(null);
  };

  // Make form editable when clicking upgrade button in header
  const handleStartUpgrade = () => {
    setIsViewOnly(false);
    setUpgradeReason('');
  };

  // Show confirmation modal when clicking upgrade button in footer
  const handleUpgrade = () => {
    const result = validateForm();
    if (!result.ok) {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (_) {}
      const msgs: string[] = [];
      Object.entries(result.errors).forEach(([key, val]) => {
        msgs.push(val);
      });
      // Map param errors to human-friendly lines
      params.forEach((p, idx) => {
        const pe = result.paramErrors[p.id] as ParamError | undefined;
        if (!pe) return;

        // Build concise, more natural fragments for the modal message only
        const fragments: string[] = [];
        if (pe.name) fragments.push('parameter name');
        if (pe.numericUnit) fragments.push('numeric unit');
        if (pe.numericMin) fragments.push('numeric min value');
        if (pe.numericMax) fragments.push('numeric max value');
        if (pe.dropdownOptions) fragments.push('at least 2 dropdown options');
        if (pe.checkboxOptions) fragments.push('at least 1 checkbox option');

        if (fragments.length) {
          msgs.push(`Parameter row ${idx + 1}: Please provide ${fragments.join(', ')}.`);
        }
        if (pe.typeRequired) {
          msgs.push(`Parameter row ${idx + 1}: Select at least one value type (Numeric, Dropdown, Checkbox, Free Text, or Remark).`);
        }
      });
      setValidationMessages(msgs);
      setShowValidationModal(true);
      return;
    }

    // If validation passes, show upgrade confirmation modal
    setShowUpgradeConfirmation(true);
  };

  // Perform the actual upgrade API call
  const performUpgrade = async () => {
    if (!upgradeReason.trim()) {
      // Show error if upgrade reason is not provided
      notification.show({
        title: 'Upgrade reason required',
        message: 'Please provide a reason for upgrading this protocol',
        type: 'error',
        duration: 5000,
        position: 'top-right',
      });
      return;
    }

    setShowUpgradeConfirmation(false);
    setLoading(true);
    try {
      if (!protocol?.id) {
        throw new Error('Protocol ID is required for upgrade');
      }

      const payload = buildProtocolPayload('Published', true);

      const response = await protocolService.upgradeProtocol(payload);
      if (response.is_success) {
        // Show success toast
        notification.show({
          title: 'Successfully upgraded',
          message: 'Protocol has been upgraded',
          type: 'success',
          duration: 3000,
          position: 'top-right',
        });
        // Refresh protocol list via callback
        onSave();
        onBack();
      } else {
        // Show error toast
        notification.show({
          title: 'Failed to upgrade protocol',
          message: response.message || 'Please try again',
          type: 'error',
          duration: 5000,
          position: 'top-right',
        });
      }
    } catch (error) {
      console.error('Error upgrading protocol:', error);
      // Show error toast
      notification.show({
        title: 'Failed to upgrade protocol',
        message: 'An error occurred. Please try again',
        type: 'error',
        duration: 5000,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelUpgrade = () => {
    setShowUpgradeConfirmation(false);
    setUpgradeReason('');
  };

  // Helper function to build protocol payload
  const buildProtocolPayload = (statusToSave: 'Published' | 'Draft', forUpgrade: boolean = false) => {
    // Map CRD ID string to numeric requirement_doc_id
    const selectedCrdDoc = crdDocs.find(doc => doc.crd_id === crdId);
    const requirementDocId = selectedCrdDoc ? selectedCrdDoc.id : parseInt(crdId, 10);

    // Helper function to recursively set is_root: false on all children
    const ensureChildrenIsRootFalse = (items: any[]): any[] => {
      return items.map(item => ({
        ...item,
        is_root: false, // Hardcode is_root to false for all children
        children: item.children ? ensureChildrenIsRootFalse(item.children) : [],
      }));
    };

    // Check if this is an update (not duplicate mode and protocol exists)
    const isUpdate = !isDuplicateMode && !!protocol?.id;

    // Convert flat rows to nested tree
    const toTree = (rows: ParamRow[], parentId?: string) =>
      rows
        .filter(r => r.parentId === parentId)
        .map(r => {
          const children = toTree(rows, r.id);
          // If parentId is defined, this is a child item - always set is_root to false
          // If parentId is undefined, this is a root-level item - use existing logic
          const isChild = parentId !== undefined;

          // Build parameter_fields with IDs for updates
          const parameterFields: any[] = [];

          if (r.numeric.enabled) {
            parameterFields.push({
              element_type: 'Numeric',
              type: r.numeric.unit || 'Numeric',
              label: 'Numeric',
              ...(r.numeric.min !== undefined || r.numeric.max !== undefined
                ? { range: { min: r.numeric.min, max: r.numeric.max } }
                : {}),
              ...(reviewType === '4 eye' && r.percentageDisparity !== undefined && r.percentageDisparity != null
                ? { disparity_threshold: r.percentageDisparity }
                : {}),
              ...(isUpdate && r.originalFieldIds?.numeric ? { parameter_field_id: String(r.originalFieldIds.numeric) } : {}),
            });
          }
          if (r.dropdown.enabled) {
            parameterFields.push({
              element_type: 'DropDown',
              type: 'Text',
              label: 'Dropdown',
              values: r.dropdown.options,
              ...(isUpdate && r.originalFieldIds?.dropdown ? { parameter_field_id: String(r.originalFieldIds.dropdown) } : {}),
            });
          }
          if (r.checkbox.enabled) {
            parameterFields.push({
              element_type: 'CheckBox',
              type: 'Text',
              label: 'CheckBoxes',
              values: r.checkbox.options,
              ...(isUpdate && r.originalFieldIds?.checkbox ? { parameter_field_id: String(r.originalFieldIds.checkbox) } : {}),
            });
          }
          if (r.freeText) {
            parameterFields.push({
              element_type: 'TextBox',
              type: 'Text',
              label: 'FreeText',
              ...(isUpdate && r.originalFieldIds?.freeText ? { parameter_field_id: String(r.originalFieldIds.freeText) } : {}),
            });
          }
          if (r.remark?.trim()) {
            parameterFields.push({
              element_type: 'TextBox',
              type: 'Text',
              label: 'Remarks',
              ...(isUpdate && r.originalFieldIds?.remark ? { parameter_field_id: String(r.originalFieldIds.remark) } : {}),
            });
          }

          const paramObj: any = {
            parameter_name: r.name,
            is_root: isChild ? false : (children.length === 0 ? true : r.isRoot), // All children must have is_root: false
            parameter_fields: parameterFields,
            children: ensureChildrenIsRootFalse(children), // Ensure all children (at any depth) have is_root: false
          };

          // Add parameter_id for updates
          if (isUpdate && r.originalParameterId) {
            paramObj.parameter_id = String(r.originalParameterId);
          }

          return paramObj;
        });

    const basePayload: any = {
      requirement_doc_id: forUpgrade ? String(requirementDocId) : requirementDocId,
      name: protocolName.trim(),
      description: description.trim(),
      version: 1,
      expected_time: Number(averageTimeForGrading), // Integer value (validation ensures it's not empty)
      fast_threshold: Number(fastThreshold),
      slow_threshold: Number(slowThreshold),
      status: statusToSave,
      is_active: isActive,
      assignee_ids: graders,
      escalate_to: forUpgrade ? String(escalateToPerson) : escalateToPerson,
      effective_start_date: effectiveFrom,
      // Map UI review type to API grading_type
      grading_type: reviewType === '4 eye' ? 'FOUR_EYES' : 'TWO_EYES',
      sampling_percentage: randomSamplingPercentage === '' ? 0 : Number(randomSamplingPercentage),
      parameters: toTree(params, undefined),
    };

    // Add upgrade-specific fields
    if (forUpgrade) {
      basePayload.protocol_id = String(protocol?.id || '');
      basePayload.upgrade_reason = upgradeReason.trim();
    }

    return basePayload;
  };


  useEffect(() => {
    if (errors.graders && graders.length > 0) {
      setErrors(prev => ({ ...prev, graders: '' }));
    }
    if (errors.escalateToPerson && escalateToPerson) {
      setErrors(prev => ({ ...prev, escalateToPerson: '' }));
    }
  }, [graders, escalateToPerson, errors.graders, errors.escalateToPerson]);

  const clearParamError = (
    id: string,
    key:
      | 'name'
      | 'numericUnit'
      | 'numericMin'
      | 'numericMax'
      | 'percentageDisparity'
      | 'dropdownOptions'
      | 'checkboxOptions'
  ) => {
    setParamErrors(prev => {
      const current = prev[id];
      if (!current || !current[key]) return prev;
      const { [key]: _omit, ...restErrs } = current;
      const next = { ...prev, [id]: restErrs } as typeof prev;
      if (Object.keys(restErrs).length === 0) {
        const { [id]: _remove, ...restMap } = next as any;
        return restMap;
      }
      return next;
    });
  };

  const handlePrintProtocol = async () => {
    const protocolId = protocol?.id ? String(protocol.id) : '';
    if (!protocolId) {
      notification.show({
        title: 'Unable to Print Protocol',
        message: 'Protocol identifier is missing.',
        type: 'error',
        position: 'top-right',
      });
      return;
    }

    if (isPrintingProtocol) {
      return;
    }

    setIsPrintingProtocol(true);
    try {
      const result = await protocolService.generateProtocolReport(protocolId);

      if (result.success) {
        notification.show({
          title: 'Protocol Report Ready',
          message: result.message,
          type: 'success',
          position: 'top-right',
          duration: 3000,
        });
      } else {
        notification.show({
          title: 'Failed to Print Protocol',
          message: result.message,
          type: 'error',
          position: 'top-right',
          duration: 4000,
        });
      }
    } catch (error) {
      console.error('Error printing protocol:', error);
      notification.show({
        title: 'Failed to Print Protocol',
        message:
          error instanceof Error ? error.message : 'Unexpected error occurred. Please try again.',
        type: 'error',
        position: 'top-right',
        duration: 4000,
      });
    } finally {
      setIsPrintingProtocol(false);
    }
  };

  return (
    <div className="admin-tab-content protocol-editor-wrapper">
      {loading && (
        <div className="protocol-editor-loading-overlay" role="status" aria-live="polite">
          <img src={loadingGif} alt="Saving..." className="loading-gif" />
        </div>
      )}
      <div
        className="protocol-form-header"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}
      >
        {/* Left: Back button */}
        <button
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'transparent',
            border: '1px solid #3a3f99',
            borderRadius: '6px',
            padding: '8px 16px',
            color: 'white',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseOver={e => {
            (e.currentTarget as HTMLElement).style.background = '#0a2a5a';
            (e.currentTarget as HTMLElement).style.borderColor = '#1454D4';
          }}
          onMouseOut={e => {
            (e.currentTarget as HTMLElement).style.background = 'transparent';
            (e.currentTarget as HTMLElement).style.borderColor = '#3a3f99';
          }}
        >
          <Icons.ArrowLeft className="h-4 w-4" />
          Back
        </button>

        {/* Center: Grader Protocol header */}
        <h3
          className="header-text"
          style={{
            margin: 0,
            color: '#cbd5e0',
            textAlign: 'center',
            flex: 1,
          }}
        >
          Grader Protocol
        </h3>

        {/* Right: Print protocol + Upgrade */}
        <div
          style={{
            minWidth: '120px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '8px',
          }}
        >
          {protocol?.id && (
            <AppTooltip content="Print Protocol">
              <button
                className="admin-button primary print-protocol-btn"
                type="button"
                aria-label="Print Protocol"
                onClick={handlePrintProtocol}
                disabled={isPrintingProtocol || loading}
                aria-busy={isPrintingProtocol}
                style={{ padding: '8px 12px' }}
              >
                <span
                  className="pdf-icon-colorful"
                  style={{ opacity: isPrintingProtocol ? 0.6 : 1 }}
                >
                  <FontAwesomeIcon icon={faFilePdf} className="pdf-fa-icon" />
                </span>
                Print
              </button>
            </AppTooltip>
          )}
          {isViewOnly && isPublished && (
            <button
              className="admin-button primary"
              onClick={handleStartUpgrade}
              disabled={loading}
              style={{ padding: '8px 12px' }}
            >
              Upgrade
            </button>
          )}
        </div>
      </div>

      {/* Grader Protocol card body */}
      <div className="protocol-form-body">
        <div
          className="admin-section"
          style={{ marginTop: 8 }}
        >
          <div
            className="search-filter-section"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 2fr 1.5fr',
              gridTemplateRows: 'auto auto auto',
              gap: '12px',
              alignItems: 'stretch',
              fontSize: '12px',
            }}
          >
            {/* Row 1 left: CRD ID, Protocol Name */}
            <div>
              <label style={{ color: 'white', display: 'block', marginBottom: 6 }}>CRD ID *</label>
              <CrdIdSearchableDropdown
                value={crdId}
                onChange={(id) => {
                  setCrdId(id);
                  if (errors.crdId) {
                    setErrors(prev => ({ ...prev, crdId: '' }));
                  }
                }}
                options={availableCrdIds}
                disabled={isViewOnly}
              />
              {errors.crdId && (
                <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                  {errors.crdId}
                </div>
              )}
            </div>

            {/* Protocol Name */}
            <div>
              <label style={{ color: 'white', display: 'block', marginBottom: 6 }}>
                Protocol Name *
              </label>
              <input
                className={`search-input ${errors.protocolName ? 'error' : ''}`}
                value={protocolName}
                onChange={e => {
                  setProtocolName(e.target.value);
                  if (errors.protocolName) {
                    setErrors(prev => ({ ...prev, protocolName: '' }));
                  }
                }}
                placeholder="DR Grading Protocol v1"
                maxLength={255}
                readOnly={isViewOnly}
                disabled={isViewOnly}
                style={{ cursor: isViewOnly ? 'not-allowed' : 'text' }}
              />
              {errors.protocolName && (
                <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                  {errors.protocolName}
                </div>
              )}
            </div>

            {/* Description tall area (spans two rows) */}
            <div
              style={{
                gridColumn: '3 / 4',
                gridRow: '1 / span 2',
                display: 'grid',
                gridTemplateRows: 'auto 1fr',
              }}
            >
              <label style={{ color: 'white', display: 'block', marginBottom: 6 }}>Description *</label>
              <textarea
                className={`search-input ${errors.description ? 'error' : ''}`}
                style={{ minHeight: 0, height: '100%', cursor: isViewOnly ? 'not-allowed' : 'text' }}
                value={description}
                onChange={e => {
                  setDescription(e.target.value);
                  if (errors.description) {
                    setErrors(prev => ({ ...prev, description: '' }));
                  }
                }}
                placeholder="Form for graders to record DR severity, CMT, fluid presence"
                maxLength={1000}
                readOnly={isViewOnly}
                disabled={isViewOnly}
              />
              {errors.description && (
                <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                  {errors.description}
                </div>
              )}
            </div>

            {/* Row 2 left: Effective From, Status */}
            <div>
              <label style={{ color: 'white', display: 'block', marginBottom: 6 }}>
                Effective From *
              </label>
              <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                <input
                  type="text"
                  className={`search-input ${errors.effectiveFrom ? 'error' : ''}`}
                  value={effectiveFrom ? moment(effectiveFrom).format('DD MMM YYYY') : ''}
                  placeholder="dd MMM yyyy"
                  readOnly
                  onClick={() => {
                    if (isViewOnly) return;
                    const picker = document.getElementById('protocolEffectiveFromPicker');
                    if (picker && 'showPicker' in picker) {
                      (picker as any).showPicker();
                    }
                  }}
                  style={{
                    cursor: isViewOnly ? 'not-allowed' : 'pointer',
                  }}
                />
                <span
                  onClick={() => {
                    if (isViewOnly) return;
                    const picker = document.getElementById('protocolEffectiveFromPicker');
                    if (picker && 'showPicker' in picker) {
                      (picker as any).showPicker();
                    }
                  }}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: isViewOnly ? 'not-allowed' : 'pointer',
                    pointerEvents: 'auto',
                    color: '#9ca3af',
                    fontSize: '16px',
                  }}
                >
                  📅
                </span>
                <input
                  id="protocolEffectiveFromPicker"
                  type="date"
                  value={effectiveFrom}
                  onChange={e => {
                    setEffectiveFrom(e.target.value);
                    if (errors.effectiveFrom) {
                      setErrors(prev => ({ ...prev, effectiveFrom: '' }));
                    }
                  }}
                  disabled={isViewOnly}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: isViewOnly ? 'not-allowed' : 'pointer',
                    pointerEvents: 'none',
                  }}
                />
              </div>
              {errors.effectiveFrom && (
                <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                  {errors.effectiveFrom}
                </div>
              )}
            </div>

            {/* Status */}
            <div>
              <label style={{ color: 'white', display: 'block', marginBottom: 6 }}>Status</label>
              <select
                className="admin-filter-select"
                value={String(isActive)}
                onChange={e => setIsActive(e.target.value === 'true')}
                disabled={isViewOnly}
                style={{ cursor: isViewOnly ? 'not-allowed' : 'pointer' }}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            {/* Row 3: Average → Fast → Slow → Review Type → Random sampling */}
            <div style={{ gridColumn: '1 / 5', gridRow: '3', minWidth: 0, width: '100%' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
                  gap: '12px',
                  width: '100%',
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <label style={{ color: 'white', display: 'block', marginBottom: 6 }}>
                    Average grading time (minutes) *
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className={`search-input ${errors.averageTimeForGrading ? 'error' : ''}`}
                    value={formatDigitsOnlyField(averageTimeForGrading)}
                    onChange={e => {
                      setAverageTimeForGrading(parseDigitsOnlyField(e.target.value));
                      if (errors.averageTimeForGrading) {
                        setErrors(prev => ({ ...prev, averageTimeForGrading: '' }));
                      }
                    }}
                    placeholder="Enter time in minutes"
                    readOnly={isViewOnly}
                    disabled={isViewOnly}
                    style={{ cursor: isViewOnly ? 'not-allowed' : 'text', width: '100%' }}
                  />
                  {errors.averageTimeForGrading && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      {errors.averageTimeForGrading}
                    </div>
                  )}
                </div>

                <div style={{ minWidth: 0 }}>
                  <label style={{ color: 'white', display: 'block', marginBottom: 6 }}>
                    Fast threshold (QC) *
                  </label>
                  <div style={{ display: 'flex', alignItems: 'stretch', width: '100%' }}>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 8px',
                        background: '#1a1f3a',
                        border: errors.fastThreshold ? '1px solid #ef4444' : '1px solid #3a3f99',
                        borderRight: 'none',
                        borderRadius: '6px 0 0 6px',
                        color: '#9ca3af',
                        fontSize: '13px',
                        userSelect: 'none',
                      }}
                    >
                      &lt;
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className={`search-input ${errors.fastThreshold ? 'error' : ''}`}
                      value={formatDigitsOnlyField(fastThreshold)}
                      onChange={e => {
                        setFastThreshold(parseDigitsOnlyField(e.target.value));
                        if (errors.fastThreshold) {
                          setErrors(prev => ({ ...prev, fastThreshold: '' }));
                        }
                      }}
                      placeholder="Minutes"
                      readOnly={isViewOnly}
                      disabled={isViewOnly}
                      style={{
                        cursor: isViewOnly ? 'not-allowed' : 'text',
                        flex: 1,
                        minWidth: 0,
                        borderRadius: 0,
                        borderLeft: 'none',
                      }}
                    />
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 6px',
                        background: '#1a1f3a',
                        border: errors.fastThreshold ? '1px solid #ef4444' : '1px solid #3a3f99',
                        borderLeft: 'none',
                        borderRadius: '0 6px 6px 0',
                        color: '#9ca3af',
                        fontSize: '11px',
                        userSelect: 'none',
                      }}
                    >
                      min
                    </span>
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '11px', marginTop: '4px', lineHeight: 1.35 }}>
                    Too fast if grading time is less than this many minutes
                  </div>
                  {errors.fastThreshold && (
                    <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>
                      {errors.fastThreshold}
                    </div>
                  )}
                </div>

                <div style={{ minWidth: 0 }}>
                  <label style={{ color: 'white', display: 'block', marginBottom: 6 }}>
                    Slow threshold (QC) *
                  </label>
                  <div style={{ display: 'flex', alignItems: 'stretch', width: '100%' }}>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 8px',
                        background: '#1a1f3a',
                        border: errors.slowThreshold ? '1px solid #ef4444' : '1px solid #3a3f99',
                        borderRight: 'none',
                        borderRadius: '6px 0 0 6px',
                        color: '#9ca3af',
                        fontSize: '13px',
                        userSelect: 'none',
                      }}
                    >
                      &gt;
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className={`search-input ${errors.slowThreshold ? 'error' : ''}`}
                      value={formatDigitsOnlyField(slowThreshold)}
                      onChange={e => {
                        setSlowThreshold(parseDigitsOnlyField(e.target.value));
                        if (errors.slowThreshold) {
                          setErrors(prev => ({ ...prev, slowThreshold: '' }));
                        }
                      }}
                      placeholder="Minutes"
                      readOnly={isViewOnly}
                      disabled={isViewOnly}
                      style={{
                        cursor: isViewOnly ? 'not-allowed' : 'text',
                        flex: 1,
                        minWidth: 0,
                        borderRadius: 0,
                        borderLeft: 'none',
                      }}
                    />
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 6px',
                        background: '#1a1f3a',
                        border: errors.slowThreshold ? '1px solid #ef4444' : '1px solid #3a3f99',
                        borderLeft: 'none',
                        borderRadius: '0 6px 6px 0',
                        color: '#9ca3af',
                        fontSize: '11px',
                        userSelect: 'none',
                      }}
                    >
                      min
                    </span>
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '11px', marginTop: '4px', lineHeight: 1.35 }}>
                    Too slow if grading time exceeds this many minutes
                  </div>
                  {errors.slowThreshold && (
                    <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>
                      {errors.slowThreshold}
                    </div>
                  )}
                </div>

                <div style={{ minWidth: 0 }}>
                  <label style={{ color: 'white', display: 'block', marginBottom: 6 }}>
                    Review Type *
                  </label>
                  <select
                    className="admin-filter-select"
                    value={reviewType}
                    onChange={e => {
                      const value = e.target.value as '2 eye' | '4 eye';
                      setReviewType(value);
                      if (value === '2 eye' && errors.graders) {
                        setErrors(prev => ({ ...prev, graders: '' }));
                      }
                    }}
                    disabled={isViewOnly}
                    style={{ width: '100%', cursor: isViewOnly ? 'not-allowed' : 'pointer' }}
                  >
                    <option value="2 eye">Two-Eye Review (Single Grader)</option>
                    <option value="4 eye">Four-Eye Review (Dual Grader)</option>
                  </select>
                </div>

                <div style={{ minWidth: 0 }}>
                  <label
                    style={{ color: 'white', display: 'block', marginBottom: 6, lineHeight: 1.3 }}
                    title="Random sampling percentage (0–100)"
                  >
                    Random sampling % *
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className={`search-input ${errors.randomSamplingPercentage ? 'error' : ''}`}
                    value={formatDigitsOnlyField(randomSamplingPercentage)}
                    onChange={e => {
                      const parsed = parseDigitsOnlyField(e.target.value);
                      if (parsed === '') {
                        setRandomSamplingPercentage('');
                      } else {
                        setRandomSamplingPercentage(Math.min(100, parsed));
                      }
                      if (errors.randomSamplingPercentage) {
                        setErrors(prev => ({ ...prev, randomSamplingPercentage: '' }));
                      }
                    }}
                    placeholder="0–100"
                    readOnly={isViewOnly}
                    disabled={isViewOnly}
                    style={{
                      cursor: isViewOnly ? 'not-allowed' : 'text',
                      width: '100%',
                    }}
                  />
                  {errors.randomSamplingPercentage && (
                    <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>
                      {errors.randomSamplingPercentage}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Select Graders and Escalate To (on right, spans two rows) */}
            <div
              style={{
                gridColumn: '4 / 5',
                gridRow: '1 / span 2',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ color: 'white', display: 'block', marginBottom: 6 }}>
                  Select Graders *
                </label>
                <GraderMultiSelect
                  value={graders}
                  onChange={ids => {
                    setGraders(ids);
                    const enoughGraders = reviewType === '4 eye' ? ids.length >= 2 : ids.length > 0;
                    if (errors.graders && enoughGraders) {
                      setErrors(prev => ({ ...prev, graders: '' }));
                    }
                  }}
                  options={availableGradersLocal}
                  disabled={isViewOnly}
                />
                {errors.graders && (
                  <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                    {errors.graders}
                  </div>
                )}
              </div>

              {/* Escalate To (mandatory) */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ color: 'white', display: 'block', marginBottom: 6 }}>
                  Escalate To *
                </label>
                <EscalateToSelect
                  value={escalateToPerson}
                  onChange={id => {
                    setEscalateToPerson(id);
                  }}
                  options={availableManagersLocal}
                  disabled={isViewOnly}
                />
                {errors.escalateToPerson && (
                  <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                    {errors.escalateToPerson}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Validation Modal */}
      {showValidationModal && (
        <div className="modal-overlay top-center" role="dialog" aria-modal="true">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Validation Errors</h3>
              <button className="admin-button secondary" onClick={() => setShowValidationModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Please fix the following issues before saving:</p>
              <ul style={{ marginTop: 8 }}>
                {validationMessages.map((m, i) => (
                  <li key={i} style={{ color: '#ef4444', marginBottom: 4 }}>{m}</li>
                ))}
              </ul>
            </div>
            <div className="modal-footer">
              <button type="button" className="admin-button primary" onClick={() => setShowValidationModal(false)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Save Confirmation Modal */}
      {showSaveConfirmation && (
        <div className="modal-overlay top-center" role="dialog" aria-modal="true">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm {pendingSaveStatus === 'Published' ? 'Publish' : 'Save'}</h3>
              <button className="admin-button secondary" onClick={handleCancelSave} disabled={loading}>×</button>
            </div>
            <div className="modal-body">
              <p>
                {pendingSaveStatus === 'Published'
                  ? 'Are you sure you want to publish this protocol? Once published, the protocol will be available for grading.'
                  : 'Are you sure you want to save this protocol as a draft? You can continue editing it later.'}
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="admin-button secondary"
                onClick={handleCancelSave}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-button primary"
                onClick={handleConfirmSave}
                disabled={loading}
              >
                {loading ? <img src={loadingGif} alt="Saving..." className="loading-gif" style={{ width: 18, height: 18 }} /> : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Confirmation Modal */}
      {showUpgradeConfirmation && (
        <div className="modal-overlay top-center" role="dialog" aria-modal="true">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Upgrade Protocol</h3>
              <button className="admin-button secondary close" onClick={handleCancelUpgrade} disabled={loading}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px' }}>Do you want to upgrade the protocol? This may affect submissions associated with it.</p>
              <div>
                <label style={{ color: 'white', display: 'block', marginBottom: '8px' }}>
                  Upgrade Reason *
                </label>
                <textarea
                  value={upgradeReason}
                  onChange={(e) => setUpgradeReason(e.target.value)}
                  placeholder="Enter the reason for upgrading this protocol"
                  maxLength={50}
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #4a5568',
                    background: '#2d3748',
                    color: 'white',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                  }}
                  disabled={loading}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="admin-button secondary"
                onClick={handleCancelUpgrade}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-button primary"
                onClick={performUpgrade}
                disabled={loading || !upgradeReason.trim()}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Parameter section with action */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 12,
          marginBottom: 4,
          gap: '12px',
        }}
      >
        <div>
          <h3 style={{ color: '#cbd5e0', margin: 0 }}>Parameter *</h3>
          {errors.parameters && (
            <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
              {errors.parameters}
            </div>
          )}
        </div>
        {!isViewOnly && (
          <button
            className="admin-button primary"
            onClick={() => addParam()}
          >
            + New Parameter
          </button>
        )}
      </div>
      <div
        className="users-table-container"
        style={{ marginTop: 4 }}
      >
        <table className="users-table protocol-params-table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: '200px', borderRight: '1px solid #3a3f99' }} className="text-left padding-x-2">Parameter</th>
              <th style={{ width: '85px', borderRight: '1px solid #3a3f99' }} className='text-center padding-x-2'>Root/Child</th>
              <th style={{ width: '210px', borderRight: '1px solid #3a3f99' }} className='text-center padding-x-2'>Numeric</th>
              <th style={{ width: '200px', borderRight: '1px solid #3a3f99' }} className='text-center padding-x-2'>Dropdown</th>
              <th style={{ width: '200px', borderRight: '1px solid #3a3f99' }} className='text-center padding-x-2'>Checkbox</th>
              <th style={{ width: '70px', borderRight: '1px solid #3a3f99' }} className='text-center padding-x-2'>Free Text</th>
              <th style={{ width: '70px', borderRight: '1px solid #3a3f99' }} className='text-center padding-x-2'>Remarks</th>
              <th style={{ width: '70px' }} className='text-center padding-x-2'>Actions</th>
            </tr>
          </thead>
          <tbody>
            {params.length === 0 ? (
              <tr>
                <td
                  className="specialization-cell padding-x-2 text-center"
                  colSpan={8}
                >
                  No Data Available
                </td>
              </tr>
            ) : (
              params.map(param => (
                <React.Fragment key={param.id}>
                <tr
                  className={`protocol-param-row${param.parentId ? ' protocol-param-row--child' : ''}`}
                >
                  <td className='padding-x-2 text-left'>
                    <input
                      className={`search-input text-left ${paramErrors[param.id]?.name ? 'error' : ''}`}
                      value={param.name}
                      onChange={e => {
                        const value = e.target.value;
                        updateParam(param.id, p => ({ ...p, name: value }));
                        if (value.trim()) {
                          clearParamError(param.id, 'name');
                        }
                      }}
                      placeholder="Parameter name"
                      maxLength={255}
                      readOnly={isViewOnly}
                      disabled={isViewOnly}
                      style={{ cursor: isViewOnly ? 'not-allowed' : 'text' }}
                    />
                    {paramErrors[param.id]?.name ? (
                      <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                        {paramErrors[param.id]?.name}
                      </div>
                    ) : null}
                  </td>
                  <td className='text-center padding-x-2'>
                    {param.isRoot ? (
                      <>
                        <input
                          type="checkbox"
                          checked={param.isRoot}
                          onChange={e => {
                            const checked = e.target.checked;
                            if (checked) {
                              // When becoming a root parameter, clear all field configurations
                              updateParam(param.id, p => ({
                                ...p,
                                isRoot: checked,
                                numeric: { enabled: false, unit: undefined, min: undefined, max: undefined },
                                dropdown: { enabled: false, options: [], input: undefined },
                                checkbox: { enabled: false, options: [], input: undefined },
                                freeText: false,
                                remark: '',
                                percentageDisparity: undefined,
                              }));
                              // Clear type-required error and other field-related errors when switching to Root
                              setParamErrors(prev => {
                                const current = prev[param.id];
                                if (!current) return prev;
                                const { typeRequired, numericUnit, dropdownOptions, checkboxOptions, ...rest } = current as any;
                                const next = { ...prev, [param.id]: rest } as typeof prev;
                                if (Object.keys(rest).length === 0) {
                                  const { [param.id]: _remove, ...restMap } = next as any;
                                  return restMap;
                                }
                                return next;
                              });
                            } else {
                              // Prevent unchecking root if it has children
                              if (childCount(param.id) > 0) {
                                return;
                              }
                              // When unchecking root, just update isRoot
                              updateParam(param.id, p => ({ ...p, isRoot: checked }));
                            }
                          }}
                          disabled={isViewOnly || childCount(param.id) > 0}
                          style={{ cursor: (isViewOnly || childCount(param.id) > 0) ? 'not-allowed' : 'pointer' }}
                        />
                        <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                          Root
                        </span>
                      </>
                    ) : param.parentId ? (
                      // Child parameter - empty cell
                      <></>
                    ) : (
                      // Regular parameter (not root, not child)
                      <>
                        <input
                          type="checkbox"
                          checked={param.isRoot}
                          onChange={e => {
                            const checked = e.target.checked;
                            if (checked) {
                              // When becoming a root parameter, clear all field configurations
                              updateParam(param.id, p => ({
                                ...p,
                                isRoot: checked,
                                numeric: { enabled: false, unit: undefined, min: undefined, max: undefined },
                                dropdown: { enabled: false, options: [], input: undefined },
                                checkbox: { enabled: false, options: [], input: undefined },
                                freeText: false,
                                remark: '',
                                percentageDisparity: undefined,
                              }));
                              // Clear type-required error and other field-related errors when switching to Root
                              setParamErrors(prev => {
                                const current = prev[param.id];
                                if (!current) return prev;
                                const { typeRequired, numericUnit, dropdownOptions, checkboxOptions, ...rest } = current as any;
                                const next = { ...prev, [param.id]: rest } as typeof prev;
                                if (Object.keys(rest).length === 0) {
                                  const { [param.id]: _remove, ...restMap } = next as any;
                                  return restMap;
                                }
                                return next;
                              });
                            } else {
                              // When unchecking root, just update isRoot
                              updateParam(param.id, p => ({ ...p, isRoot: checked }));
                            }
                          }}
                          disabled={isViewOnly}
                          style={{ cursor: isViewOnly ? 'not-allowed' : 'pointer' }}
                        />
                        <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                          Root
                        </span>
                      </>
                    )}
                  </td>
                  <td className='text-center padding-x-2'>
                    {(!param.isRoot || param.parentId) && (
                      <div className='justify-content-center' style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', height: '100%', padding: '4px 0' }}>
                        <input
                          type="checkbox"
                          checked={param.numeric.enabled}
                          onChange={e => {
                            const enabled = e.target.checked;
                            updateParam(param.id, p => ({
                              ...p,
                              numeric: { ...p.numeric, enabled },
                            }));
                            if (!enabled) {
                              clearParamError(param.id, 'numericUnit');
                              clearParamError(param.id, 'numericMin');
                              clearParamError(param.id, 'numericMax');
                            }
                          }}
                          disabled={isViewOnly}
                          style={{ margin: 0, marginTop: '4px', verticalAlign: 'top', cursor: isViewOnly ? 'not-allowed' : 'pointer', flexShrink: 0 }}
                        />
                        {param.numeric.enabled && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 }}>
                            <select
                              className={`search-input ${paramErrors[param.id]?.numericUnit ? 'error' : ''}`}
                              value={param.numeric.unit || ''}
                              onChange={e => {
                                const unit = e.target.value;
                                updateParam(param.id, p => ({
                                  ...p,
                                  numeric: { ...p.numeric, unit },
                                }));
                                if (unit && unit.trim()) {
                                  clearParamError(param.id, 'numericUnit');
                                }
                              }}
                              disabled={isViewOnly}
                              style={{ cursor: isViewOnly ? 'not-allowed' : 'pointer', width: '100%' }}
                            >
                              <option value="">Select Unit</option>
                              {NUMERIC_UNIT_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            {paramErrors[param.id]?.numericUnit ? (
                              <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                                {paramErrors[param.id]?.numericUnit}
                              </div>
                            ) : null}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', width: '100%' }}>
                                <span style={{ fontSize: '12px', fontWeight: 600, flexShrink: 0 }}>Range:</span>
                                <input
                                  type="text"
                                  className={`search-input ${paramErrors[param.id]?.numericMin ? 'error' : ''}`}
                                  placeholder="Min"
                                  value={param.numeric.min !== undefined ? String(param.numeric.min) : ''}
                                  onChange={e => {
                                    const digits = e.target.value.replace(/\D/g, '');
                                    const min = digits === '' ? undefined : Number(digits);
                                    updateParam(param.id, p => ({
                                      ...p,
                                      numeric: { ...p.numeric, min },
                                    }));
                                    if (min !== undefined && min !== null) {
                                      clearParamError(param.id, 'numericMin');
                                    }
                                  }}
                                  disabled={isViewOnly}
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  style={{ width: '80px', cursor: isViewOnly ? 'not-allowed' : 'text', flexShrink: 0 }}
                                />
                                <span style={{ fontSize: '12px', flexShrink: 0 }}>to</span>
                                <input
                                  type="text"
                                  className={`search-input ${paramErrors[param.id]?.numericMax ? 'error' : ''}`}
                                  placeholder="Max"
                                  value={param.numeric.max !== undefined ? String(param.numeric.max) : ''}
                                  onChange={e => {
                                    const digits = e.target.value.replace(/\D/g, '');
                                    const max = digits === '' ? undefined : Number(digits);
                                    updateParam(param.id, p => ({
                                      ...p,
                                      numeric: { ...p.numeric, max },
                                    }));
                                    if (max !== undefined && max !== null) {
                                      clearParamError(param.id, 'numericMax');
                                    }
                                  }}
                                  disabled={isViewOnly}
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  style={{ width: '80px', cursor: isViewOnly ? 'not-allowed' : 'text', flexShrink: 0 }}
                                />
                              </div>
                              {(paramErrors[param.id]?.numericMin || paramErrors[param.id]?.numericMax) && (
                                <div style={{ color: '#ef4444', fontSize: '12px' }}>
                                  {paramErrors[param.id]?.numericMin || paramErrors[param.id]?.numericMax}
                                </div>
                              )}
                                {reviewType === '4 eye' && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                                  <span style={{ fontSize: '12px', fontWeight: 600, flexShrink: 0 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                                      <span>Disparity threshold:</span>
                                      {param.numeric.unit ? (
                                        <span
                                          style={{
                                            fontSize: '11px',
                                            fontWeight: 500,
                                            color: '#cbd5e0',
                                            textTransform: 'none',
                                          }}
                                        >
                                          {`in ${
                                            NUMERIC_UNIT_OPTIONS.find(
                                              o => String(o.value).toLowerCase() === String(param.numeric.unit).toLowerCase()
                                            )?.label || String(param.numeric.unit).toLowerCase()
                                          }`}
                                        </span>
                                      ) : null}
                                    </div>
                                  </span>
                                  <input
                                    type="number"
                                      className={`search-input ${paramErrors[param.id]?.percentageDisparity ? 'error' : ''}`}
                                    placeholder="e.g. 2.5"
                                    value={param.percentageDisparity ?? ''}
                                    onChange={e => {
                                      const v = e.target.value;
                                      updateParam(param.id, p => ({
                                        ...p,
                                        percentageDisparity: v === '' ? undefined : Number(v),
                                      }));
                                        if (v !== '' && paramErrors[param.id]?.percentageDisparity) {
                                          clearParamError(param.id, 'percentageDisparity');
                                        }
                                    }}
                                    disabled={isViewOnly}
                                    min={0}
                                    max={100}
                                    step="0.1"
                                    style={{ width: '80px', cursor: isViewOnly ? 'not-allowed' : 'text', flexShrink: 0 }}
                                  />
                                </div>
                              )}
                                {reviewType === '4 eye' && paramErrors[param.id]?.percentageDisparity && (
                                  <div style={{ color: '#ef4444', fontSize: '12px' }}>
                                    {paramErrors[param.id]?.percentageDisparity}
                                  </div>
                                )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className='text-center padding-x-2'>
                    {(!param.isRoot || param.parentId) && (
                      <div className='justify-content-center' style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '100%' }}>
                        <input
                          type="checkbox"
                          checked={param.dropdown.enabled}
                          onChange={e => {
                            const enabled = e.target.checked;
                            updateParam(param.id, p => ({
                              ...p,
                              dropdown: { ...p.dropdown, enabled },
                            }));
                            if (!enabled) {
                              clearParamError(param.id, 'dropdownOptions');
                            }
                          }}
                          disabled={isViewOnly}
                          style={{ margin: 0, verticalAlign: 'middle', cursor: isViewOnly ? 'not-allowed' : 'pointer' }}
                        />
                        {param.dropdown.enabled && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                              <input
                                className="search-input"
                                value={param.dropdown.input || ''}
                                onChange={e => updateParam(param.id, p => ({
                                  ...p,
                                  dropdown: { ...p.dropdown, input: e.target.value }
                                }))}
                                placeholder="Add option"
                                maxLength={100}
                                readOnly={isViewOnly}
                                disabled={isViewOnly}
                                style={{ cursor: isViewOnly ? 'not-allowed' : 'text' }}

                              />
                              {!isViewOnly && (
                              <button
                                className="action-button edit"
                                onClick={() => {
                                  addOption(param.id, 'dropdown');
                                  // After add, if list has 2+, clear error
                                  const target = params.find(p => p.id === param.id);
                                  const count = (target?.dropdown.options?.length || 0) + (target?.dropdown.input?.trim() ? 1 : 0);
                                  if (count >= 2) {
                                    clearParamError(param.id, 'dropdownOptions');
                                  }
                                }}
                                disabled={!param.dropdown.input?.trim()}
                                style={{ verticalAlign: 'middle', margin: 0, backgroundColor: '#1454d4', color: '#ffffff' }}
                              >
                                +
                              </button>
                              )}
                            </div>
                            {param.dropdown.options.length > 0 && (
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                {param.dropdown.options.map((option, idx) => (
                                  <span key={idx} className="param-chip">
                                    {option}
                                    {!isViewOnly && (
                                    <button
                                      type="button"
                                      className="action-button delete"
                                      onClick={() => updateParam(param.id, p => ({
                                        ...p,
                                        dropdown: {
                                          ...p.dropdown,
                                          options: p.dropdown.options.filter((_, i) => i !== idx)
                                        }
                                      }))}
                                    >
                                      ×
                                    </button>
                                    )}
                                  </span>
                                ))}
                              </div>
                            )}
                            {paramErrors[param.id]?.dropdownOptions ? (
                              <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                                {paramErrors[param.id]?.dropdownOptions}
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className='text-center padding-x-2'>
                    {(!param.isRoot || param.parentId) && (
                      <div className='justify-content-center' style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '100%' }}>
                        <input
                          type="checkbox"
                          checked={param.checkbox.enabled}
                          onChange={e => {
                            const enabled = e.target.checked;
                            updateParam(param.id, p => ({
                              ...p,
                              checkbox: { ...p.checkbox, enabled },
                            }));
                            if (!enabled) {
                              clearParamError(param.id, 'checkboxOptions');
                            }
                          }}
                          disabled={isViewOnly}
                          style={{ margin: 0, verticalAlign: 'middle', cursor: isViewOnly ? 'not-allowed' : 'pointer' }}
                        />
                        {param.checkbox.enabled && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                              <input
                                className="search-input"
                                value={param.checkbox.input || ''}
                                onChange={e => updateParam(param.id, p => ({
                                  ...p,
                                  checkbox: { ...p.checkbox, input: e.target.value }
                                }))}
                                placeholder="Add option"
                                maxLength={100}
                                readOnly={isViewOnly}
                                disabled={isViewOnly}
                                style={{ cursor: isViewOnly ? 'not-allowed' : 'text' }}

                              />
                              {!isViewOnly && (
                              <button
                                className="action-button edit"
                                onClick={() => {
                                  addOption(param.id, 'checkbox');
                                  const target = params.find(p => p.id === param.id);
                                  const count = (target?.checkbox.options?.length || 0) + (target?.checkbox.input?.trim() ? 1 : 0);
                                  if (count >= 1) {
                                    clearParamError(param.id, 'checkboxOptions');
                                  }
                                }}
                                disabled={!param.checkbox.input?.trim()}
                                style={{ verticalAlign: 'middle', margin: 0, backgroundColor: '#1454d4', color: '#ffffff' }}
                              >
                                +
                              </button>
                              )}
                            </div>
                            {param.checkbox.options.length > 0 && (
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                {param.checkbox.options.map((option, idx) => (
                                  <span key={idx} className="param-chip">
                                    {option}
                                    {!isViewOnly && (
                                    <button
                                      type="button"
                                      className="action-button delete"
                                      onClick={() => updateParam(param.id, p => ({
                                        ...p,
                                        checkbox: {
                                          ...p.checkbox,
                                          options: p.checkbox.options.filter((_, i) => i !== idx)
                                        }
                                      }))}
                                    >
                                      ×
                                    </button>
                                    )}
                                  </span>
                                ))}
                              </div>
                            )}
                            {paramErrors[param.id]?.checkboxOptions ? (
                              <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                                {paramErrors[param.id]?.checkboxOptions}
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className='text-center padding-x-2'>
                    {(!param.isRoot || param.parentId) && (
                      <input
                        type="checkbox"
                        checked={param.freeText}
                        onChange={e => updateParam(param.id, p => ({ ...p, freeText: e.target.checked }))}
                        disabled={isViewOnly}
                        style={{ cursor: isViewOnly ? 'not-allowed' : 'pointer' }}
                      />
                    )}
                  </td>
                  <td className='text-center padding-x-2'>
                    {(!param.isRoot || param.parentId) && (
                      <input
                        type="checkbox"
                        checked={param.remark === 'enabled'}
                        onChange={e => updateParam(param.id, p => ({
                          ...p,
                          remark: e.target.checked ? 'enabled' : ''
                        }))}
                        disabled={isViewOnly}
                        style={{ cursor: isViewOnly ? 'not-allowed' : 'pointer' }}
                      />
                    )}
                  </td>
                  <td className='text-center padding-x-2'>
                    <div className='justify-content-center' style={{ display: 'flex', gap: '2px'}}>
                      {param.isRoot && !isViewOnly && (
                        <button
                          className="admin-button primary"
                          style={{ fontSize: '11px', padding: '4px 8px', height: 'auto' }}
                          title="Add Child"
                          onClick={() => addParam(param.id)}
                        >
                          +Child
                        </button>
                      )}
                      {!isViewOnly && (
                       <button
                          className="action-button delete"
                          title="Delete Parameter"
                          onClick={() => {
                            // Recursive function to collect all descendant IDs
                            const collectDescendantIds = (parentId: string, allParams: ParamRow[]): string[] => {
                              const children = allParams.filter(p => p.parentId === parentId);
                              let descendantIds = children.map(c => c.id);
                              // Recursively collect grandchildren and deeper descendants
                              children.forEach(child => {
                                descendantIds = descendantIds.concat(collectDescendantIds(child.id, allParams));
                              });
                              return descendantIds;
                            };

                            // Get all descendant IDs (children, grandchildren, etc.)
                            const descendantIds = collectDescendantIds(param.id, params);

                            // Delete the parameter and all its descendants
                            setParams(prev => prev.filter(p => p.id !== param.id && !descendantIds.includes(p.id)));
                          }}
                        >
                          <span className="action-icon">🗑️</span>
                        </button>
                      )}
                      {/* <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}> */}
                        {/* <button
                          className="action-button delete"
                          title="Delete Parameter"
                          onClick={() => setParams(prev => prev.filter(p => p.id !== param.id))}
                        >
                          <span className="action-icon">🗑️</span>
                        </button> */}
                      {/* </div> */}
                    </div>
                  </td>
                </tr>
                {paramErrors[param.id]?.typeRequired && (
                  <tr>
                    <td colSpan={8}>
                      <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                        {paramErrors[param.id]?.typeRequired}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="protocol-form-footer">
        <button
          className="admin-button secondary"
          onClick={onBack}
        >
          {isViewOnly ? 'Close' : 'Cancel'}
        </button>
        {!isViewOnly && (
          <>
            {/* Show Upgrade button only when editing an existing published protocol (not in duplicate mode) */}
            {isPublished && !isDuplicateMode ? (
              <button
                className="admin-button primary"
                onClick={handleUpgrade}
                disabled={loading}
                style={{ marginLeft: 8 }}
              >
                Upgrade
              </button>
            ) : (
              <>
                <button
                  className="admin-button secondary"
                  onClick={() => handleSave('Draft')}
                  disabled={loading}
                  style={{ marginLeft: 8 }}
                >
                  Save as Draft
                </button>
                <button
                  className="admin-button primary"
                  onClick={() => handleSave('Published')}
                  disabled={loading}
                  style={{ marginLeft: 8 }}
                >
                  Save and Publish
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
